import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import Banner from '../../ui/Banner';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import knowledgeApi, { getKnowledgeErrorMessage } from './api/knowledgeApi';
import './KnowledgeBaseView.css';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
    ['blockquote', 'code-block'],
    ['clean'],
  ],
};

const quillFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'check',
  'blockquote',
  'code-block',
];

export function cleanHtmlForDisplay(str) {
  if (!str || typeof str !== 'string') return '';
  if (!/<[a-z][\s\S]*>/i.test(str) && !str.includes('&nbsp;')) {
    return str;
  }
  return str
    .replace(/&nbsp;/g, ' ')
    .replace(/<h1[^>]*>([\s\S]*?)<\/h1>/gi, '# $1\n\n')
    .replace(/<h2[^>]*>([\s\S]*?)<\/h2>/gi, '## $1\n\n')
    .replace(/<h3[^>]*>([\s\S]*?)<\/h3>/gi, '### $1\n\n')
    .replace(/<h4[^>]*>([\s\S]*?)<\/h4>/gi, '#### $1\n\n')
    .replace(/<h5[^>]*>([\s\S]*?)<\/h5>/gi, '##### $1\n\n')
    .replace(/<h6[^>]*>([\s\S]*?)<\/h6>/gi, '###### $1\n\n')
    .replace(/<strong>([\s\S]*?)<\/strong>/gi, '**$1**')
    .replace(/<b>([\s\S]*?)<\/b>/gi, '**$1**')
    .replace(/<em>([\s\S]*?)<\/em>/gi, '*$1*')
    .replace(/<i>([\s\S]*?)<\/i>/gi, '*$1*')
    .replace(/<u>([\s\S]*?)<\/u>/gi, '$1')
    .replace(/<s>([\s\S]*?)<\/s>/gi, '~~$1~~')
    .replace(/<strike>([\s\S]*?)<\/strike>/gi, '~~$1~~')
    .replace(/<li[^>]*data-list="unchecked"[^>]*>([\s\S]*?)<\/li>/gi, '- [ ] $1\n')
    .replace(/<li[^>]*data-list="checked"[^>]*>([\s\S]*?)<\/li>/gi, '- [x] $1\n')
    .replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, '- $1\n')
    .replace(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/gi, '> $1\n\n')
    .replace(/<pre[^>]*><code[^>]*>([\s\S]*?)<\/code><\/pre>/gi, '```\n$1\n```\n\n')
    .replace(/<code[^>]*>([\s\S]*?)<\/code>/gi, '`$1`')
    .replace(/<p><br\s*\/?><\/p>/gi, '\n')
    .replace(/<p>([\s\S]*?)<\/p>/gi, '$1\n\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<img[^>]*src=["']([^"']+)["'][^>]*alt=["']([^"']*)["'][^>]*>/gi, '![$2]($1)\n\n')
    .replace(/<img[^>]*alt=["']([^"']*)["'][^>]*src=["']([^"']+)["'][^>]*>/gi, '![$1]($2)\n\n')
    .replace(/<img[^>]*src=["']([^"']+)["'][^>]*>/gi, '![]($1)\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function formatCategoryTitle(category) {
  if (!category) return 'General Evaluation';
  return category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getCategoryIcon(category) {
  const cat = String(category || '').toLowerCase();
  if (cat.includes('adversarial') || cat.includes('injection') || cat.includes('defense')) {
    return 'fa-shield-halved';
  }
  if (cat.includes('abstain') || cat.includes('scope')) {
    return 'fa-hand';
  }
  if (cat.includes('hallucination') || cat.includes('factual')) {
    return 'fa-brain';
  }
  if (cat.includes('leakage') || cat.includes('tenant')) {
    return 'fa-user-lock';
  }
  if (cat.includes('expired') || cat.includes('outdated')) {
    return 'fa-clock-rotate-left';
  }
  return 'fa-bullseye';
}

export function formatCorrectiveAction(action) {
  switch (action) {
    case 'verified_knowledge':
      return 'Verified & renewed source';
    case 'added_replacement_source':
      return 'Added replacement source';
    case 'superseded_outdated_source':
      return 'Marked outdated source superseded';
    case 'updated_retrieval_config':
      return 'Adjusted retrieval ranking / threshold';
    case 'added_eval_case':
      return 'Added to regression evaluation dataset';
    case 'no_action_needed':
      return 'No knowledge change required';
    default:
      return (action || 'Corrective action').replace(/_/g, ' ');
  }
}

export function formatFeedbackDate(dateVal) {
  if (!dateVal) return 'Recently';
  try {
    const d = new Date(dateVal);
    if (Number.isNaN(d.getTime())) return 'Recently';
    return d.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (_) {
    return 'Recently';
  }
}

export function cleanVersion(version) {
  if (!version) return '—';
  return String(version).replace(/^minimax:/i, '');
}

export function renderReasonBadge(reason) {
  switch (reason) {
    case 'incorrect_answer':
      return (
        <span className="knowledge-reason-badge incorrect">
          <i className="fa-solid fa-circle-xmark"></i> Incorrect Answer
        </span>
      );
    case 'outdated_information':
      return (
        <span className="knowledge-reason-badge outdated">
          <i className="fa-solid fa-clock-rotate-left"></i> Outdated Info
        </span>
      );
    case 'missing_information':
      return (
        <span className="knowledge-reason-badge missing">
          <i className="fa-solid fa-file-circle-question"></i> Missing Info
        </span>
      );
    case 'irrelevant_information':
      return (
        <span className="knowledge-reason-badge irrelevant">
          <i className="fa-solid fa-filter-circle-xmark"></i> Irrelevant Info
        </span>
      );
    case 'incorrect_citation':
      return (
        <span className="knowledge-reason-badge citation">
          <i className="fa-solid fa-quote-left"></i> Bad Citation
        </span>
      );
    default:
      return (
        <span className="knowledge-reason-badge default">
          <i className="fa-solid fa-circle-info"></i> {(reason || 'General').replace(/_/g, ' ')}
        </span>
      );
  }
}

export function requiresReviewAction(source) {
  if (!source) return false;
  if (source.status === 'needs_review') return true;
  const approval = source.approval_status || source.approvalStatus;
  if (approval === 'pending_approval') return true;
  const freshness = source.freshness_status || source.freshnessStatus;
  if (freshness === 'review_due' || freshness === 'pending_verification') return true;
  const reviewDueAt = source.review_due_at || source.reviewDueAt;
  if (reviewDueAt) {
    const revTime = new Date(reviewDueAt).getTime();
    if (!Number.isNaN(revTime) && revTime <= Date.now()) {
      return true;
    }
  }
  return false;
}

export default function KnowledgeBaseView() {
  const {
    workspaceId,
    userId,
    isOwner,
    isOrganizationAdmin,
    isSalesManager,
    workspaceRole,
    username,
    workspaceName,
  } = useWorkspace();

  const isReviewer = Boolean(
    isOwner ||
    isOrganizationAdmin ||
    isSalesManager ||
    ['org_owner', 'org_admin', 'org_sales_manager', 'owner', 'admin', 'sales_manager'].includes(
      workspaceRole
    )
  );

  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState({
    totalSources: 0,
    totalChunks: 0,
    totalTokens: 0,
    approvedCount: 0,
    pendingCount: 0,
    rejectedCount: 0,
    indexedCount: 0,
    processingCount: 0,
    reviewCount: 0,
    categoryCounts: {},
  });

  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingVisibilityId, setUpdatingVisibilityId] = useState(null);
  const [deletingSourceTarget, setDeletingSourceTarget] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [knowledgeTypeFilter, setKnowledgeTypeFilter] = useState('all');
  const [freshnessFilter, setFreshnessFilter] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Freshness & Supersession state (Module A)
  const [verifyingId, setVerifyingId] = useState(null);
  const [supersedingSource, setSupersedingSource] = useState(null);
  const [supersedeReplacementId, setSupersedeReplacementId] = useState('');
  const [submittingSupersede, setSubmittingSupersede] = useState(false);

  // Rejection modal state
  const [rejectingSource, setRejectingSource] = useState(null);
  const [rejectionReasonInput, setRejectionReasonInput] = useState('');
  const [submittingReject, setSubmittingReject] = useState(false);
  const [viewingRejectionSource, setViewingRejectionSource] = useState(null);

  // Live Source Viewer state
  const [previewSource, setPreviewSource] = useState(null);
  const [previewFullSource, setPreviewFullSource] = useState(null);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewActiveTab, setPreviewActiveTab] = useState('content'); // 'content' | 'chunks'
  const [previewPdfViewMode, setPreviewPdfViewMode] = useState('pdf'); // 'pdf' | 'text'
  const [previewChunkSearch, setPreviewChunkSearch] = useState('');
  const [copiedPreviewContent, setCopiedPreviewContent] = useState(false);
  const [copiedChunkId, setCopiedChunkId] = useState(null);

  // Modal form state
  const [sourceType, setSourceType] = useState('file'); // 'file' | 'web' | 'text'
  const [formTitle, setFormTitle] = useState('');
  const [formKnowledgeType, setFormKnowledgeType] = useState('internal');
  const [formDescription, setFormDescription] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formUrl, setFormUrl] = useState('');
  const [formEffectiveFrom, setFormEffectiveFrom] = useState('');
  const [formReviewDueAt, setFormReviewDueAt] = useState('');
  const [formExpiresAt, setFormExpiresAt] = useState('');
  const [formSupersedesSourceId, setFormSupersedesSourceId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const todayStr = new Date().toLocaleDateString('en-CA');

  // RAG Q&A Assistant state
  const [questionInput, setQuestionInput] = useState('');
  const [askingQuestion, setAskingQuestion] = useState(false);
  const [ragResult, setRagResult] = useState(null);
  const [ragError, setRagError] = useState(null);
  const [activeCitation, setActiveCitation] = useState(null);
  const [copiedAnswer, setCopiedAnswer] = useState(false);
  const [copiedPassage, setCopiedPassage] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null); // { src, alt }

  // RAG User Feedback state (Module B)
  const [ragFeedbackRating, setRagFeedbackRating] = useState(null); // 'helpful' | 'not_helpful'
  const [ragFeedbackReason, setRagFeedbackReason] = useState('incorrect_answer');
  const [ragFeedbackComment, setRagFeedbackComment] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  // Quality & Evaluation Loop state (Modules B3, C, D)
  const [qualityDashboard, setQualityDashboard] = useState(null);
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [evalCases, setEvalCases] = useState([]);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [runningEval, setRunningEval] = useState(false);
  const [runningSweep, setRunningSweep] = useState(false);
  const [downloadingEvalPdf, setDownloadingEvalPdf] = useState(false);
  const [fbFilterRating, setFbFilterRating] = useState('all');
  const [fbFilterReason, setFbFilterReason] = useState('all');
  const [fbFilterStatus, setFbFilterStatus] = useState('all');
  const [fbSearchQuery, setFbSearchQuery] = useState('');
  const [fbViewMode, setFbViewMode] = useState('cards');
  const [resolvingFeedbackItem, setResolvingFeedbackItem] = useState(null);
  const [resolveStatusInput, setResolveStatusInput] = useState('resolved');
  const [resolveActionInput, setResolveActionInput] = useState('verified_knowledge');
  const [resolveNotesInput, setResolveNotesInput] = useState('');
  const [resolvePromoteEval, setResolvePromoteEval] = useState(false);
  const [submittingResolveFb, setSubmittingResolveFb] = useState(false);

  // Add Evaluation Case modal state (Module C1)
  const [isAddEvalCaseModalOpen, setIsAddEvalCaseModalOpen] = useState(false);
  const [evalCaseQuestion, setEvalCaseQuestion] = useState('');
  const [evalCaseExpectedAnswer, setEvalCaseExpectedAnswer] = useState('');
  const [evalCaseExpectedSourceId, setEvalCaseExpectedSourceId] = useState('');
  const [evalCaseExpectedKeywords, setEvalCaseExpectedKeywords] = useState('');
  const [evalCaseCategory, setEvalCaseCategory] = useState('correct_retrieval');
  const [evalCaseExpectedBehavior, setEvalCaseExpectedBehavior] = useState('answer');
  const [submittingEvalCase, setSubmittingEvalCase] = useState(false);

  // Modify / Remove Evaluation Case state (Module C1 & C3)
  const [editingEvalCase, setEditingEvalCase] = useState(null);
  const [editCaseQuestion, setEditCaseQuestion] = useState('');
  const [editCaseExpectedAnswer, setEditCaseExpectedAnswer] = useState('');
  const [editCaseExpectedSourceId, setEditCaseExpectedSourceId] = useState('');
  const [editCaseExpectedKeywords, setEditCaseExpectedKeywords] = useState('');
  const [editCaseCategory, setEditCaseCategory] = useState('correct_retrieval');
  const [editCaseExpectedBehavior, setEditCaseExpectedBehavior] = useState('answer');
  const [submittingUpdateCase, setSubmittingUpdateCase] = useState(false);
  const [deletingCaseId, setDeletingCaseId] = useState(null);
  const [deletingEvalCaseTarget, setDeletingEvalCaseTarget] = useState(null);

  const handleCopyAnswer = () => {
    if (!ragResult?.answer) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(ragResult.answer);
    }
    setCopiedAnswer(true);
    setTimeout(() => setCopiedAnswer(false), 2000);
  };

  const handleCopyPassage = () => {
    const rawText = activeCitation?.content || activeCitation?.excerpt;
    const text = cleanHtmlForDisplay(rawText);
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    }
    setCopiedPassage(true);
    setTimeout(() => setCopiedPassage(false), 2000);
  };

  // Convert bracketed citations like [C1] into clickable markdown links when evidence is grounded
  const formattedAnswer = useMemo(() => {
    if (!ragResult?.answer) return '';
    if (!ragResult?.hasEvidence) return ragResult.answer;
    return ragResult.answer.replace(/\[C(\d+)\]/gi, (match, p1) => {
      return `[${match}](#citation-${p1})`;
    });
  }, [ragResult?.answer, ragResult?.hasEvidence]);

  const markdownComponents = useMemo(
    () => ({
      a: ({ href, children, ...props }) => {
        if (href && href.startsWith('#citation-')) {
          const num = parseInt(href.replace('#citation-', ''), 10);
          const citation = ragResult?.hasEvidence
            ? ragResult?.citations?.find((c) => c.citationIndex === num)
            : null;
          if (!citation) {
            return <span className="knowledge-inline-citation-badge disabled">{children}</span>;
          }
          return (
            <button
              type="button"
              className="knowledge-inline-citation-badge"
              onClick={(e) => {
                e.preventDefault();
                if (citation) setActiveCitation(citation);
              }}
              title={`Source: ${citation.sourceTitle}`}
            >
              {children}
            </button>
          );
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
          </a>
        );
      },
      table: ({ node, ...props }) => (
        <div className="knowledge-table-wrap">
          <table {...props} />
        </div>
      ),
      img: ({ node, ...props }) => (
        <span className="knowledge-passage-img-container">
          <img
            {...props}
            className="knowledge-passage-img"
            alt={props.alt || 'Diagram / Image'}
            loading="lazy"
            onClick={() => setZoomedImage({ src: props.src, alt: props.alt })}
            title="Click to view full image in high resolution"
          />
        </span>
      ),
    }),
    [ragResult?.citations]
  );

  const passageMarkdownComponents = useMemo(
    () => ({
      table: ({ node, ...props }) => (
        <div className="knowledge-table-wrap">
          <table {...props} />
        </div>
      ),
      img: ({ node, ...props }) => (
        <span className="knowledge-passage-img-container">
          <img
            {...props}
            className="knowledge-passage-img"
            alt={props.alt || 'Grounded source diagram'}
            loading="lazy"
            onClick={() => setZoomedImage({ src: props.src, alt: props.alt })}
            title="Click to view full image in high resolution"
          />
        </span>
      ),
    }),
    []
  );

  const isCitationDownloadable = useMemo(() => {
    if (!activeCitation?.sourceId) return false;
    const sourceInList = sources.find((s) => s.id === activeCitation.sourceId);
    if (sourceInList) {
      return Boolean(
        sourceInList.storage_key ||
        (sourceInList.original_filename &&
          sourceInList.source_type !== 'web' &&
          sourceInList.source_type !== 'text' &&
          sourceInList.format !== 'URL' &&
          sourceInList.format !== 'WEB')
      );
    }
    if (activeCitation.hasFile !== undefined) {
      return Boolean(activeCitation.hasFile);
    }
    if (activeCitation.storageKey) return true;
    const format = String(activeCitation.sourceFormat || '').toUpperCase();
    if (format === 'URL' || format === 'WEB' || format === 'TEXT') {
      return false;
    }
    if (activeCitation.sourceUrl && !activeCitation.originalFilename) {
      return false;
    }
    return Boolean(
      activeCitation.originalFilename ||
      (activeCitation.sourceFormat && ['PDF', 'DOCX', 'TXT', 'MD'].includes(format))
    );
  }, [activeCitation, sources]);

  // Fetch sources and stats from backend
  const loadData = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      setError(null);

      let approvalStatusParam = 'approved';
      if (activeTab === 'pending_approval' || activeTab === 'rejected') {
        approvalStatusParam = activeTab;
      } else {
        approvalStatusParam = 'approved';
      }

      const categoryParam = categoryFilter !== 'all' ? categoryFilter : undefined;
      const freshnessParam = freshnessFilter !== 'all' ? freshnessFilter : undefined;
      const knowledgeTypeParam = knowledgeTypeFilter !== 'all' ? knowledgeTypeFilter : undefined;

      const [fetchedSources, fetchedStats] = await Promise.all([
        knowledgeApi.getSources(workspaceId, {
          category: categoryParam,
          approvalStatus: approvalStatusParam,
          freshnessStatus: freshnessParam,
          knowledgeType: knowledgeTypeParam,
          search: searchQuery.trim() || undefined,
        }),
        knowledgeApi.getStats(workspaceId),
      ]);
      setSources(fetchedSources || []);
      if (fetchedStats) {
        setStats(fetchedStats);
      }
    } catch (err) {
      console.error('Failed to load knowledge base data:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to load knowledge base assets.'));
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [workspaceId, activeTab, categoryFilter, freshnessFilter, knowledgeTypeFilter, searchQuery]);

  const loadQualityLoopData = useCallback(async () => {
    if (!workspaceId || !isReviewer) return;
    try {
      setQualityLoading(true);
      const [dash, fbList, cases] = await Promise.all([
        knowledgeApi.getQualityDashboard
          ? knowledgeApi.getQualityDashboard(workspaceId)
          : Promise.resolve(null),
        knowledgeApi.listFeedback
          ? knowledgeApi.listFeedback(workspaceId, {
              rating: fbFilterRating,
              reason: fbFilterReason,
              status: fbFilterStatus,
            })
          : Promise.resolve([]),
        knowledgeApi.getEvalCases ? knowledgeApi.getEvalCases(workspaceId) : Promise.resolve([]),
      ]);
      if (dash) setQualityDashboard(dash);
      setFeedbackItems(fbList || []);
      setEvalCases(cases || []);
    } catch (err) {
      console.error('Failed to load RAG quality loop data:', err);
    } finally {
      setQualityLoading(false);
    }
  }, [workspaceId, isReviewer, fbFilterRating, fbFilterReason, fbFilterStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'quality_loop' && isReviewer) {
      loadQualityLoopData();
    }
  }, [activeTab, isReviewer, loadQualityLoopData]);

  const feedbackMetrics = useMemo(() => {
    const summary = qualityDashboard?.feedbackSummary || {};
    const total = summary.totalFeedback ?? feedbackItems.length;
    const helpful =
      summary.helpfulCount ?? feedbackItems.filter((f) => f.rating === 'helpful').length;
    const notHelpful =
      summary.notHelpfulCount ?? feedbackItems.filter((f) => f.rating === 'not_helpful').length;
    const openAction =
      (summary.newCount ?? 0) +
        (summary.actionRequiredCount ?? 0) +
        (summary.underReviewCount ?? 0) ||
      feedbackItems.filter((f) => f.rating === 'not_helpful' && f.status !== 'resolved').length;
    const resolved =
      summary.resolvedCount ?? feedbackItems.filter((f) => f.status === 'resolved').length;
    const helpfulPct = total > 0 ? Math.round((helpful / total) * 100) : 100;
    return { total, helpful, notHelpful, openAction, resolved, helpfulPct };
  }, [qualityDashboard, feedbackItems]);

  const displayedFeedbackItems = useMemo(() => {
    if (!fbSearchQuery.trim()) return feedbackItems;
    const q = fbSearchQuery.trim().toLowerCase();
    return feedbackItems.filter((item) => {
      const question = String(item.question || '').toLowerCase();
      const comment = String(item.comment || '').toLowerCase();
      const resolution = String(item.resolution_notes || '').toLowerCase();
      const reason = String(item.reason || '').toLowerCase();
      const source = String(item.linked_source_title || '').toLowerCase();
      return (
        question.includes(q) ||
        comment.includes(q) ||
        resolution.includes(q) ||
        reason.includes(q) ||
        source.includes(q)
      );
    });
  }, [feedbackItems, fbSearchQuery]);

  // Tab counts based on stats categoryCounts or fallback to sources list
  const counts = useMemo(() => {
    const catMap = stats.categoryCounts || {};
    const fMap = stats.freshnessCounts || {};
    return {
      all: stats.approvedCount || 0,
      documents: catMap.documents || 0,
      web: catMap.web || 0,
      pending: stats.pendingCount || 0,
      approved: stats.approvedCount || 0,
      rejected: stats.rejectedCount || 0,
      attention: (fMap.reviewDue || 0) + (fMap.expired || 0),
    };
  }, [stats]);

  // Filter sources for sales members when viewing pending approval
  const displayedSources = useMemo(() => {
    if (!isReviewer && activeTab === 'pending_approval') {
      return sources.filter((item) => {
        const isCreator =
          (item.created_by_user_id && item.created_by_user_id === userId) ||
          (item.created_by && item.created_by === username);
        return isCreator && (item.approval_status || 'pending_approval') === 'pending_approval';
      });
    }
    return sources;
  }, [sources, isReviewer, activeTab, userId, username]);

  const handleApproveSource = async (id) => {
    try {
      setApprovingId(id);
      setError(null);
      await knowledgeApi.approveSource(workspaceId, id);
      setSuccessMessage('Knowledge source approved and published for production RAG retrieval.');
      setTimeout(() => setSuccessMessage(null), 4000);
      await loadData();
    } catch (err) {
      console.error('Failed to approve knowledge source:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to approve knowledge source.'));
    } finally {
      setApprovingId(null);
    }
  };

  const handleVerifySource = async (id, title) => {
    try {
      setVerifyingId(id);
      setError(null);
      await knowledgeApi.verifySource(workspaceId, id, {});
      setSuccessMessage(
        `Knowledge source "${title || 'Asset'}" verified and renewed for production RAG retrieval.`
      );
      setTimeout(() => setSuccessMessage(null), 4000);
      await loadData();
      if (activeTab === 'quality_loop') {
        await loadQualityLoopData();
      }
    } catch (err) {
      console.error('Failed to verify knowledge source:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to verify knowledge source.'));
    } finally {
      setVerifyingId(null);
    }
  };

  const handleToggleKnowledgeType = async (source) => {
    if (!source || !workspaceId || updatingVisibilityId) return;
    const currentType = source.knowledge_type === 'external' ? 'external' : 'internal';
    const nextType = currentType === 'external' ? 'internal' : 'external';
    const sourceId = source.id;

    try {
      setUpdatingVisibilityId(sourceId);
      setError(null);

      // Optimistic update
      setSources((prev) =>
        prev.map((s) => (s.id === sourceId ? { ...s, knowledge_type: nextType } : s))
      );
      if (previewSource && previewSource.id === sourceId) {
        setPreviewSource((prev) => ({ ...prev, knowledge_type: nextType }));
      }
      if (previewFullSource && previewFullSource.id === sourceId) {
        setPreviewFullSource((prev) => ({ ...prev, knowledge_type: nextType }));
      }

      await knowledgeApi.updateSourceVisibility(workspaceId, sourceId, nextType);

      setSuccessMessage(
        `Visibility changed to ${nextType.toUpperCase()} for "${source.title || 'Knowledge Source'}".`
      );
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      console.error('Failed to update knowledge visibility:', err);
      // Rollback optimistic update
      setSources((prev) =>
        prev.map((s) => (s.id === sourceId ? { ...s, knowledge_type: currentType } : s))
      );
      if (previewSource && previewSource.id === sourceId) {
        setPreviewSource((prev) => ({ ...prev, knowledge_type: currentType }));
      }
      if (previewFullSource && previewFullSource.id === sourceId) {
        setPreviewFullSource((prev) => ({ ...prev, knowledge_type: currentType }));
      }
      setError(getKnowledgeErrorMessage(err, 'Failed to update knowledge visibility.'));
    } finally {
      setUpdatingVisibilityId(null);
    }
  };

  const handleConfirmSupersede = async (e) => {
    e.preventDefault();
    if (!supersedingSource || !supersedeReplacementId) return;
    try {
      setSubmittingSupersede(true);
      setError(null);
      await knowledgeApi.supersedeSource(workspaceId, supersedingSource.id, supersedeReplacementId);
      setSuccessMessage(
        `"${supersedingSource.title}" marked as superseded and excluded from production RAG retrieval.`
      );
      setTimeout(() => setSuccessMessage(null), 4000);
      setSupersedingSource(null);
      setSupersedeReplacementId('');
      await loadData();
    } catch (err) {
      console.error('Failed to supersede knowledge source:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to supersede knowledge source.'));
    } finally {
      setSubmittingSupersede(false);
    }
  };

  const handleOpenRejectModal = (source) => {
    setRejectingSource(source);
    setRejectionReasonInput('');
  };

  const handleConfirmReject = async (e) => {
    e.preventDefault();
    if (!rejectingSource || !rejectionReasonInput.trim()) return;

    try {
      setSubmittingReject(true);
      setError(null);
      await knowledgeApi.rejectSource(workspaceId, rejectingSource.id, rejectionReasonInput.trim());
      setSuccessMessage(`Knowledge source "${rejectingSource.title}" has been rejected.`);
      setTimeout(() => setSuccessMessage(null), 4000);
      setRejectingSource(null);
      setRejectionReasonInput('');
      await loadData();
    } catch (err) {
      console.error('Failed to reject knowledge source:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to reject knowledge source.'));
    } finally {
      setSubmittingReject(false);
    }
  };

  const handleOpenPreview = async (source) => {
    setPreviewSource(source);
    setPreviewFullSource(null);
    setPreviewFileUrl(null);
    setPreviewActiveTab('content');
    setPreviewPdfViewMode('pdf');
    setPreviewChunkSearch('');

    const reviewActionRequired = requiresReviewAction(source) || source.accessRestricted;
    if (reviewActionRequired && !isReviewer) {
      setPreviewLoading(false);
      setPreviewFullSource({
        ...source,
        content:
          'This information required review. You cannot access this knowledge until it has been reviewed and verified by an authorized reviewer.',
        chunks: [],
        accessRestricted: true,
      });
      return;
    }

    setPreviewLoading(true);
    try {
      const full = await knowledgeApi.getSource(workspaceId, source.id);
      if (full) {
        setPreviewFullSource(full);
      }

      if (
        (source.storage_key || source.original_filename) &&
        (!reviewActionRequired || isReviewer) &&
        !full?.accessRestricted
      ) {
        const fileRes = await knowledgeApi.getSourceFile(workspaceId, source.id);
        if (fileRes?.downloadUrl) {
          setPreviewFileUrl(fileRes.downloadUrl);
        }
      }
    } catch (err) {
      console.error('Failed to load live preview details:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCopyPreviewText = () => {
    const rawText =
      previewFullSource?.content || previewSource?.content || previewSource?.description || '';
    const text = cleanHtmlForDisplay(rawText);
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    }
    setCopiedPreviewContent(true);
    setTimeout(() => setCopiedPreviewContent(false), 2000);
  };

  const handleCopyChunk = (chunkId, content) => {
    const text = cleanHtmlForDisplay(content);
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    }
    setCopiedChunkId(chunkId);
    setTimeout(() => setCopiedChunkId(null), 2000);
  };

  const displayedChunks = useMemo(() => {
    const rawChunks = previewFullSource?.chunks || [];
    if (!previewChunkSearch.trim()) return rawChunks;
    const q = previewChunkSearch.toLowerCase().trim();
    return rawChunks.filter(
      (ch) =>
        (ch.content && ch.content.toLowerCase().includes(q)) ||
        (ch.heading && ch.heading.toLowerCase().includes(q)) ||
        String(ch.page_number || '').includes(q) ||
        String((ch.chunk_index || 0) + 1).includes(q)
    );
  }, [previewFullSource?.chunks, previewChunkSearch]);

  const docStats = useMemo(() => {
    const rawText =
      previewFullSource?.content || previewSource?.content || previewSource?.description || '';
    const text = cleanHtmlForDisplay(rawText);
    const words = text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
    const readMinutes = Math.max(1, Math.ceil(words / 200));
    return {
      words,
      readMinutes,
      tokens:
        previewSource?.token_count ??
        previewSource?.tokenCount ??
        Math.ceil((text.length || 1) / 4),
      chunks: previewFullSource?.chunks?.length || previewSource?.chunks_count || 0,
    };
  }, [previewFullSource?.content, previewSource]);

  const handleDeleteSource = (sourceOrId) => {
    if (typeof sourceOrId === 'object' && sourceOrId !== null) {
      setDeletingSourceTarget(sourceOrId);
    } else {
      const found = sources.find((it) => it.id === sourceOrId);
      setDeletingSourceTarget(found || { id: sourceOrId, title: 'Selected Source' });
    }
  };

  const handleConfirmDeleteSource = async () => {
    if (!deletingSourceTarget) return;
    const id = deletingSourceTarget.id;
    try {
      setDeletingId(id);
      setError(null);
      await knowledgeApi.deleteSource(workspaceId, id);
      setSuccessMessage('Knowledge source permanently deleted.');
      setTimeout(() => setSuccessMessage(null), 4000);
      setDeletingSourceTarget(null);
      await loadData();
    } catch (err) {
      console.error('Failed to delete knowledge source:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to delete knowledge source.'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleSyncVectors = async () => {
    try {
      setSyncing(true);
      setError(null);
      const updatedStats = await knowledgeApi.syncVectors(workspaceId);
      if (updatedStats) {
        setStats(updatedStats);
      }
      setSuccessMessage('Vector index synchronized and up-to-date.');
      setTimeout(() => setSuccessMessage(null), 4000);
      await loadData();
    } catch (err) {
      console.error('Failed to synchronize vectors:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to synchronize vectors.'));
    } finally {
      setSyncing(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      if (!formTitle.trim()) {
        const base = file.name.replace(/\.[^/.]+$/, '');
        setFormTitle(base);
      }
    }
  };

  const handleSaveSource = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setError(null);

      if (formEffectiveFrom && formEffectiveFrom < todayStr) {
        setError('Effective From Date cannot be in the past.');
        setSubmitting(false);
        return;
      }
      if (formReviewDueAt && formReviewDueAt < todayStr) {
        setError('Review Due Date cannot be in the past.');
        setSubmitting(false);
        return;
      }
      if (formExpiresAt && formExpiresAt < todayStr) {
        setError('Expiration Date cannot be in the past.');
        setSubmitting(false);
        return;
      }
      if (formEffectiveFrom && formReviewDueAt && formReviewDueAt < formEffectiveFrom) {
        setError('Review Due Date cannot be earlier than Effective From Date.');
        setSubmitting(false);
        return;
      }
      if (formReviewDueAt && formExpiresAt && formExpiresAt < formReviewDueAt) {
        setError('Expiration Date cannot be earlier than Review Due Date.');
        setSubmitting(false);
        return;
      }

      const optionalFreshnessFields = {
        ...(formEffectiveFrom ? { effectiveFrom: formEffectiveFrom } : {}),
        ...(formReviewDueAt ? { reviewDueAt: formReviewDueAt } : {}),
        ...(formExpiresAt ? { expiresAt: formExpiresAt } : {}),
        ...(formSupersedesSourceId ? { supersedesSourceId: formSupersedesSourceId } : {}),
      };

      if (sourceType === 'file') {
        if (!selectedFile) {
          setError('Please select a file to upload.');
          setSubmitting(false);
          return;
        }

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('title', formTitle.trim() || selectedFile.name);
        if (formDescription.trim()) {
          formData.append('description', formDescription.trim());
        }
        formData.append('category', 'documents');
        formData.append('knowledgeType', formKnowledgeType);
        if (formEffectiveFrom) formData.append('effectiveFrom', formEffectiveFrom);
        if (formReviewDueAt) formData.append('reviewDueAt', formReviewDueAt);
        if (formExpiresAt) formData.append('expiresAt', formExpiresAt);
        if (formSupersedesSourceId) formData.append('supersedesSourceId', formSupersedesSourceId);

        await knowledgeApi.uploadSource(workspaceId, formData);
        setSuccessMessage(
          `Document "${selectedFile.name}" submitted for approval and vector indexed.`
        );
      } else if (sourceType === 'web') {
        if (!formTitle.trim()) {
          setError('Please enter a source title.');
          setSubmitting(false);
          return;
        }
        if (!formUrl.trim()) {
          setError('Please enter a target URL.');
          setSubmitting(false);
          return;
        }

        const payload = {
          title: formTitle.trim(),
          description: formDescription.trim(),
          sourceType: 'web',
          category: 'web',
          format: 'URL',
          sourceUrl: formUrl.trim(),
          content: null,
          knowledgeType: formKnowledgeType,
          ...optionalFreshnessFields,
        };

        await knowledgeApi.createSource(workspaceId, payload);
        setSuccessMessage(
          `Web page "${formTitle.trim()}" submitted for approval and vector indexed.`
        );
      } else {
        // sourceType === 'text'
        if (!formTitle.trim()) {
          setError('Please enter a source title.');
          setSubmitting(false);
          return;
        }
        const cleanContent = cleanHtmlForDisplay(formContent);
        if (!cleanContent) {
          setError('Please enter the document / notes content.');
          setSubmitting(false);
          return;
        }

        const payload = {
          title: formTitle.trim(),
          description: formDescription.trim(),
          sourceType: 'documents',
          category: 'documents',
          format: 'MARKDOWN',
          sourceUrl: null,
          content: cleanContent,
          knowledgeType: formKnowledgeType,
          ...optionalFreshnessFields,
        };

        await knowledgeApi.createSource(workspaceId, payload);
        setSuccessMessage(
          `Knowledge source "${formTitle.trim()}" submitted for approval and vector indexed.`
        );
      }

      setTimeout(() => setSuccessMessage(null), 4000);

      setIsAddModalOpen(false);
      // Reset form
      setFormTitle('');
      setFormKnowledgeType('internal');
      setFormDescription('');
      setFormContent('');
      setFormUrl('');
      setFormEffectiveFrom('');
      setFormReviewDueAt('');
      setFormExpiresAt('');
      setFormSupersedesSourceId('');
      setSelectedFile(null);
      setSourceType('file');
      if (fileInputRef.current) fileInputRef.current.value = '';

      await loadData();
    } catch (err) {
      console.error('Failed to create knowledge source:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to create and index knowledge source.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAskQuestion = async (e) => {
    e.preventDefault();
    if (!questionInput.trim()) return;

    try {
      setAskingQuestion(true);
      setRagError(null);
      setRagFeedbackRating(null);
      setRagFeedbackComment('');
      setFeedbackSubmitted(false);
      const res = await knowledgeApi.askQuestion(workspaceId, {
        question: questionInput.trim(),
      });
      setRagResult(res);
    } catch (err) {
      console.error('Failed to ask question:', err);
      setRagError(getKnowledgeErrorMessage(err, 'Failed to generate grounded answer.'));
    } finally {
      setAskingQuestion(false);
    }
  };

  const handleSelectFeedbackRating = async (rating) => {
    if (!ragResult) return;
    setRagFeedbackRating(rating);
    if (rating === 'helpful') {
      try {
        setSubmittingFeedback(true);
        await knowledgeApi.submitFeedback(workspaceId, {
          requestId: ragResult.requestId,
          question: ragResult.question || questionInput.trim(),
          answerExcerpt: (ragResult.answer || '').slice(0, 500),
          retrievedSourceIds:
            ragResult.retrievedSourceIds ||
            (ragResult.citations || []).map((c) => c.sourceId).filter(Boolean),
          retrievedChunkIds:
            ragResult.retrievedChunkIds ||
            (ragResult.citations || []).map((c) => c.chunkId).filter(Boolean),
          rating: 'helpful',
          modelVersion: ragResult.modelVersion,
          promptVersion: ragResult.promptVersion,
          retrievalConfigVersion: ragResult.retrievalConfigVersion,
        });
        setFeedbackSubmitted(true);
      } catch (err) {
        console.error('Failed to submit helpful feedback:', err);
      } finally {
        setSubmittingFeedback(false);
      }
    }
  };

  const handleSubmitNegativeFeedback = async (e) => {
    e.preventDefault();
    if (!ragResult) return;
    try {
      setSubmittingFeedback(true);
      await knowledgeApi.submitFeedback(workspaceId, {
        requestId: ragResult.requestId,
        question: ragResult.question || questionInput.trim(),
        answerExcerpt: (ragResult.answer || '').slice(0, 500),
        retrievedSourceIds:
          ragResult.retrievedSourceIds ||
          (ragResult.citations || []).map((c) => c.sourceId).filter(Boolean),
        retrievedChunkIds:
          ragResult.retrievedChunkIds ||
          (ragResult.citations || []).map((c) => c.chunkId).filter(Boolean),
        rating: 'not_helpful',
        reason: ragFeedbackReason,
        comment: ragFeedbackComment.trim() || null,
        modelVersion: ragResult.modelVersion,
        promptVersion: ragResult.promptVersion,
        retrievalConfigVersion: ragResult.retrievalConfigVersion,
      });
      setFeedbackSubmitted(true);
    } catch (err) {
      console.error('Failed to submit negative feedback:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to submit RAG feedback.'));
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleRunFreshnessSweep = async () => {
    try {
      setRunningSweep(true);
      setError(null);
      const res = await knowledgeApi.runFreshnessSweep(workspaceId);
      setSuccessMessage(
        `Freshness sweep complete: ${res?.expiredTransitioned || 0} expired, ${res?.reviewDueTransitioned || 0} review due.`
      );
      setTimeout(() => setSuccessMessage(null), 4000);
      await loadData();
      await loadQualityLoopData();
    } catch (err) {
      console.error('Failed to run freshness sweep:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to run freshness sweep.'));
    } finally {
      setRunningSweep(false);
    }
  };

  const handleRunEvaluation = async () => {
    try {
      setRunningEval(true);
      setError(null);
      const run = await knowledgeApi.runEvaluation(workspaceId, { topK: 5 });
      setSuccessMessage(
        `RAG Evaluation completed: ${run?.passed_cases ?? run?.passedCases ?? 0}/${run?.total_cases ?? run?.totalCases ?? 0} cases passed (${
          run?.regression_passed !== false ? 'No Regressions' : 'Regression Detected'
        }).`
      );
      setTimeout(() => setSuccessMessage(null), 4000);
      await loadQualityLoopData();
    } catch (err) {
      console.error('Failed to run RAG evaluation:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to execute RAG evaluation run.'));
    } finally {
      setRunningEval(false);
    }
  };

  const handleDownloadEvalReport = async () => {
    if (!workspaceId) return;
    try {
      setDownloadingEvalPdf(true);
      setError(null);
      const runId = qualityDashboard?.latestEvaluation?.id || 'latest';
      const runRef = (runId === 'latest' ? 'latest' : String(runId).slice(0, 8)).toUpperCase();
      const defaultFilename = `AI_LeadGen_RAG_Evaluation_Report_${runRef}.pdf`;
      await knowledgeApi.downloadEvaluationReport(
        workspaceId,
        runId,
        defaultFilename,
        workspaceName
      );
    } catch (err) {
      console.error('Failed to download evaluation PDF report:', err);
      setError(
        getKnowledgeErrorMessage(err, 'Failed to download evaluation report in PDF format.')
      );
    } finally {
      setDownloadingEvalPdf(false);
    }
  };

  const handleCreateEvalCase = async (e) => {
    e.preventDefault();
    if (!evalCaseQuestion.trim()) return;
    try {
      setSubmittingEvalCase(true);
      setError(null);
      const keywords = evalCaseExpectedKeywords
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean);
      await knowledgeApi.createEvalCase(workspaceId, {
        question: evalCaseQuestion.trim(),
        expectedAnswer: evalCaseExpectedAnswer.trim() || null,
        expectedSourceIds: evalCaseExpectedSourceId ? [evalCaseExpectedSourceId] : [],
        expectedKeywords: keywords,
        evalCategory: evalCaseCategory,
        expectedBehavior: evalCaseExpectedBehavior,
      });
      setSuccessMessage('Evaluation test case added to dataset.');
      setTimeout(() => setSuccessMessage(null), 4000);
      setIsAddEvalCaseModalOpen(false);
      setEvalCaseQuestion('');
      setEvalCaseExpectedAnswer('');
      setEvalCaseExpectedSourceId('');
      setEvalCaseExpectedKeywords('');
      setEvalCaseCategory('correct_retrieval');
      setEvalCaseExpectedBehavior('answer');
      await loadQualityLoopData();
    } catch (err) {
      console.error('Failed to create evaluation case:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to create evaluation case.'));
    } finally {
      setSubmittingEvalCase(false);
    }
  };

  const handleOpenModifyEvalCase = (c) => {
    setEditingEvalCase(c);
    setEditCaseQuestion(c.question || '');
    setEditCaseExpectedAnswer(c.expected_answer || c.reference_answer || '');
    setEditCaseExpectedSourceId(
      Array.isArray(c.expected_source_ids) && c.expected_source_ids.length > 0
        ? c.expected_source_ids[0]
        : ''
    );
    setEditCaseExpectedKeywords(
      Array.isArray(c.expected_keywords) ? c.expected_keywords.join(', ') : ''
    );
    setEditCaseCategory(c.eval_category || c.category || 'correct_retrieval');
    setEditCaseExpectedBehavior(
      c.expected_behavior === 'abstain' || c.expected_behavior === 'exclude_expired'
        ? c.expected_behavior
        : 'answer'
    );
  };

  const handleUpdateEvalCase = async (e) => {
    e.preventDefault();
    if (!editingEvalCase || !editCaseQuestion.trim()) return;
    try {
      setSubmittingUpdateCase(true);
      setError(null);
      const keywords = editCaseExpectedKeywords
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean);

      const payload = {
        question: editCaseQuestion.trim(),
        expectedAnswer: editCaseExpectedAnswer.trim() || null,
        expectedSourceIds: editCaseExpectedSourceId ? [editCaseExpectedSourceId] : [],
        expectedKeywords: keywords,
        evalCategory: editCaseCategory,
        expectedBehavior:
          editCaseExpectedBehavior === 'answer'
            ? 'answer_with_citations'
            : editCaseExpectedBehavior,
      };

      const updated = await knowledgeApi.updateEvalCase(workspaceId, editingEvalCase.id, payload);
      setEvalCases((prev) =>
        prev.map((item) => (item.id === editingEvalCase.id ? { ...item, ...updated } : item))
      );
      setSuccessMessage('Evaluation test case updated successfully.');
      setTimeout(() => setSuccessMessage(null), 4000);
      setEditingEvalCase(null);
      await loadQualityLoopData();
    } catch (err) {
      console.error('Failed to update evaluation case:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to update evaluation case.'));
    } finally {
      setSubmittingUpdateCase(false);
    }
  };

  const handleDeleteEvalCase = (caseOrId) => {
    if (typeof caseOrId === 'object' && caseOrId !== null) {
      setDeletingEvalCaseTarget(caseOrId);
    } else {
      const found = evalCases.find((ec) => ec.id === caseOrId);
      setDeletingEvalCaseTarget(found || { id: caseOrId, question: 'Selected Test Case' });
    }
  };

  const handleConfirmDeleteEvalCase = async () => {
    if (!deletingEvalCaseTarget) return;
    const caseId = deletingEvalCaseTarget.id;
    try {
      setDeletingCaseId(caseId);
      setError(null);
      await knowledgeApi.deleteEvalCase(workspaceId, caseId);
      setEvalCases((prev) => prev.filter((item) => item.id !== caseId));
      setSuccessMessage('Evaluation test case removed from dataset.');
      setTimeout(() => setSuccessMessage(null), 4000);
      setDeletingEvalCaseTarget(null);
      await loadQualityLoopData();
    } catch (err) {
      console.error('Failed to remove evaluation case:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to remove evaluation test case.'));
    } finally {
      setDeletingCaseId(null);
    }
  };

  const handleConfirmResolveFeedback = async (e) => {
    e.preventDefault();
    if (!resolvingFeedbackItem) return;
    try {
      setSubmittingResolveFb(true);
      setError(null);
      await knowledgeApi.resolveFeedback(workspaceId, resolvingFeedbackItem.id, {
        status: resolveStatusInput,
        correctiveAction: resolveActionInput,
        resolutionNotes: resolveNotesInput.trim(),
        promoteToEvalCase: resolvePromoteEval,
      });
      setSuccessMessage('RAG feedback updated and quality action recorded.');
      setTimeout(() => setSuccessMessage(null), 4000);
      setResolvingFeedbackItem(null);
      setResolveNotesInput('');
      setResolvePromoteEval(false);
      await loadQualityLoopData();
    } catch (err) {
      console.error('Failed to update feedback:', err);
      setError(getKnowledgeErrorMessage(err, 'Failed to update feedback status.'));
    } finally {
      setSubmittingResolveFb(false);
    }
  };

  const handleDownloadFile = async (id, title) => {
    const src =
      sources.find((s) => s.id === id) || (previewSource?.id === id ? previewSource : null);
    if (src && requiresReviewAction(src) && !isReviewer) {
      setError(
        'This information required review. You cannot access or download this knowledge until it has been reviewed and verified.'
      );
      return;
    }
    try {
      const fileData = await knowledgeApi.getSourceFile(workspaceId, id);
      if (fileData?.url) {
        window.open(fileData.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setError(`Could not download file: ${getKnowledgeErrorMessage(err)}`);
    }
  };

  const getFormatIcon = (format) => {
    switch (String(format || '').toUpperCase()) {
      case 'PDF':
        return 'fa-file-pdf';
      case 'DOCX':
        return 'fa-file-word';
      case 'URL':
      case 'WEB':
        return 'fa-globe';
      case 'FAQ':
        return 'fa-comments';
      case 'MARKDOWN':
      case 'MD':
        return 'fa-file-lines';
      default:
        return 'fa-book';
    }
  };

  const getFormatBadgeClass = (format) => {
    switch (String(format || '').toUpperCase()) {
      case 'PDF':
        return 'format-pdf';
      case 'DOCX':
        return 'format-docx';
      case 'URL':
      case 'WEB':
        return 'format-web';
      case 'MARKDOWN':
      case 'MD':
        return 'format-md';
      default:
        return 'format-doc';
    }
  };

  return (
    <div className="knowledge-view-container">
      {/* ── Page Header Banner ── */}
      <Banner
        title="Knowledge Base & RAG"
        subtitle="Manage, organize, and retrieve verified knowledge assets for grounded AI intelligence."
        type="page-header"
      />

      {/* ── Modern Floating Toast Notifications ── */}
      {(error || successMessage) && (
        <div className="knowledge-toast-container">
          {error && (
            <div className="knowledge-toast error">
              <div className="knowledge-toast-icon">
                <i className="fa-solid fa-triangle-exclamation"></i>
              </div>
              <div className="knowledge-toast-content">
                <span className="knowledge-toast-title">Operation Notice</span>
                <span className="knowledge-toast-message">{error}</span>
              </div>
              <button
                type="button"
                className="knowledge-toast-close"
                onClick={() => setError(null)}
                aria-label="Dismiss message"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          )}

          {successMessage && (
            <div className="knowledge-toast success">
              <div className="knowledge-toast-icon">
                <i className="fa-solid fa-circle-check"></i>
              </div>
              <div className="knowledge-toast-content">
                <span className="knowledge-toast-title">Success</span>
                <span className="knowledge-toast-message">{successMessage}</span>
              </div>
              <button
                type="button"
                className="knowledge-toast-close"
                onClick={() => setSuccessMessage(null)}
                aria-label="Dismiss message"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── KPI Strip ── */}
      <div className="knowledge-kpi-grid">
        <div className="knowledge-kpi-card">
          <div className="knowledge-kpi-icon blue">
            <i className="fa-solid fa-layer-group"></i>
          </div>
          <div className="knowledge-kpi-info">
            <span className="knowledge-kpi-label">Approved Assets</span>
            <span className="knowledge-kpi-value">{stats.approvedCount || 0}</span>
          </div>
        </div>

        <div className="knowledge-kpi-card">
          <div className="knowledge-kpi-icon amber">
            <i className="fa-solid fa-clock"></i>
          </div>
          <div className="knowledge-kpi-info">
            <span className="knowledge-kpi-label">Pending Approval</span>
            <span className="knowledge-kpi-value">{stats.pendingCount || 0}</span>
          </div>
        </div>

        <div className="knowledge-kpi-card">
          <div className="knowledge-kpi-icon purple">
            <i className="fa-solid fa-database"></i>
          </div>
          <div className="knowledge-kpi-info">
            <span className="knowledge-kpi-label">Embedded Chunks</span>
            <span className="knowledge-kpi-value">{stats.totalChunks || 0}</span>
          </div>
        </div>

        <div className="knowledge-kpi-card">
          <div className="knowledge-kpi-icon green">
            <i className="fa-solid fa-shield-halved"></i>
          </div>
          <div className="knowledge-kpi-info">
            <span className="knowledge-kpi-label">RAG Grounding</span>
            <span className="knowledge-kpi-value">
              {stats.approvedCount > 0 ? `${stats.approvedCount} Active` : '0 Active'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Interactive RAG Q&A Assistant ── */}
      <div className="knowledge-rag-section">
        <div className="knowledge-rag-header">
          <div>
            <h3 className="knowledge-rag-title">
              <i className="fa-solid fa-robot text-blue"></i>
              Ask Knowledge Base (Grounded RAG)
            </h3>
            <p className="knowledge-rag-subtitle">
              Semantic vector search grounded strictly in approved knowledge. The model refuses to
              hallucinate if evidence is insufficient.
            </p>
          </div>
        </div>

        <form onSubmit={handleAskQuestion} className="knowledge-rag-form">
          <input
            type="text"
            className="knowledge-rag-input"
            placeholder="Ask a question grounded in your authorized knowledge base..."
            value={questionInput}
            onChange={(e) => setQuestionInput(e.target.value)}
            disabled={askingQuestion}
          />
          <button
            type="submit"
            className="knowledge-btn knowledge-btn-primary"
            disabled={askingQuestion || !questionInput.trim()}
          >
            <i
              className={`fa-solid ${askingQuestion ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'}`}
            ></i>
            {askingQuestion ? 'Searching & Grounding...' : 'Ask AI'}
          </button>
        </form>

        {ragError && (
          <div className="knowledge-alert error mt-3">
            <span>
              <i className="fa-solid fa-circle-exclamation mr-2"></i>
              {ragError}
            </span>
          </div>
        )}

        {ragResult && (
          <div className="knowledge-rag-answer-box">
            <div className="knowledge-rag-answer-header">
              <div className="knowledge-rag-answer-title">
                <i className="fa-solid fa-sparkles text-blue"></i>
                <span>Answer to: “ {ragResult.question} ”</span>
              </div>
              <div className="knowledge-rag-answer-badges">
                <button
                  type="button"
                  className="knowledge-rag-copy-btn"
                  onClick={handleCopyAnswer}
                  title="Copy answer to clipboard"
                >
                  <i
                    className={`fa-solid ${copiedAnswer ? 'fa-check text-green' : 'fa-clipboard'}`}
                  ></i>
                  <span>{copiedAnswer ? 'Copied' : 'Copy'}</span>
                </button>
                {ragResult.requiresReview ||
                String(ragResult.answer || '').includes('This information required review') ? (
                  <span
                    className="knowledge-rag-evidence-pill review_required"
                    data-testid="rag-review-required-pill"
                  >
                    <i className="fa-solid fa-triangle-exclamation"></i>
                    Review Required — Access Restricted
                  </span>
                ) : (
                  <span
                    className={`knowledge-rag-evidence-pill ${
                      ragResult.hasEvidence ? 'grounded' : 'insufficient'
                    }`}
                  >
                    <i
                      className={`fa-solid ${
                        ragResult.hasEvidence ? 'fa-check-circle' : 'fa-circle-exclamation'
                      }`}
                    ></i>
                    {ragResult.hasEvidence
                      ? `Grounded in ${ragResult.citations?.length || 0} Sources`
                      : 'Insufficient Evidence'}
                  </span>
                )}
              </div>
            </div>

            {ragResult.requiresReview ||
            String(ragResult.answer || '').includes('This information required review') ? (
              <div className="knowledge-rag-review-card" data-testid="rag-review-alert">
                <div className="knowledge-rag-review-card-header">
                  <div className="knowledge-rag-review-card-icon-box">
                    <i className="fa-solid fa-shield-halved"></i>
                  </div>
                  <div className="knowledge-rag-review-card-header-text">
                    <div className="knowledge-rag-review-card-tags">
                      <span className="knowledge-rag-review-badge">
                        <i className="fa-solid fa-lock"></i>
                        Governance Checkpoint
                      </span>
                      <span className="knowledge-rag-review-status-chip">
                        <i className="fa-solid fa-clock-rotate-left"></i>
                        Verification Required
                      </span>
                    </div>
                    <h4 className="knowledge-rag-review-card-title">
                      Access Restricted &mdash; Knowledge Review Required
                    </h4>
                  </div>
                </div>

                <div className="knowledge-rag-review-card-body">
                  <div className="knowledge-rag-review-notice-box">
                    <p className="knowledge-rag-review-primary-text">
                      <strong>Notice: </strong>This information required review. You cannot access
                      this knowledge until it has been reviewed and verified by an authorized
                      reviewer.
                    </p>
                  </div>
                  <p className="knowledge-rag-review-secondary-text">
                    To maintain strict enterprise compliance and data accuracy, AI answers cannot be
                    synthesized from knowledge records with pending approvals, expired validity, or
                    scheduled freshness reviews.
                  </p>
                </div>

                <div className="knowledge-rag-review-card-footer">
                  <div className="knowledge-rag-review-guidance-pill">
                    <i className="fa-solid fa-user-shield text-amber-600"></i>
                    <span>
                      Authorized Reviewers:{' '}
                      <strong>Workspace Owner, Admin, or Sales Manager</strong>
                    </span>
                  </div>
                  <div className="knowledge-rag-review-guidance-pill">
                    <i className="fa-solid fa-file-shield text-slate-400"></i>
                    <span>Document content, vector chunks, and citations remain secured</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="knowledge-rag-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {formattedAnswer}
                </ReactMarkdown>
              </div>
            )}

            {ragResult.hasEvidence && ragResult.citations && ragResult.citations.length > 0 && (
              <div className="knowledge-citations-list">
                <span className="knowledge-citations-label">
                  <i className="fa-solid fa-quote-left text-blue"></i>
                  <span>Verified Sources Cited:</span>
                </span>
                <div className="knowledge-citations-grid">
                  {ragResult.citations.map((c, idx) => (
                    <div
                      key={idx}
                      className="knowledge-citation-card"
                      onClick={() => setActiveCitation(c)}
                      title="Click to view citation excerpt"
                    >
                      <span className="knowledge-citation-index">
                        [C{c.citationIndex || idx + 1}]
                      </span>
                      <span className="font-medium">{c.sourceTitle}</span>
                      {c.pageNumber && (
                        <span className="knowledge-citation-page">P. {c.pageNumber}</span>
                      )}
                      {typeof c.similarity === 'number' && (
                        <span className="knowledge-citation-match">
                          {Math.round(c.similarity * 100)}% Match
                        </span>
                      )}
                      <i className="fa-solid fa-arrow-up-right-from-square ml-1 text-slate-400"></i>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Module B: User Feedback for RAG Responses ── */}
            <div className="knowledge-rag-feedback-bar" data-testid="rag-feedback-bar">
              <div className="knowledge-rag-feedback-top">
                <div className="knowledge-rag-feedback-prompt">
                  <i className="fa-regular fa-comment-dots text-slate-400"></i>
                  <span>Was this AI response helpful and well-grounded?</span>
                </div>

                {feedbackSubmitted ? (
                  <span className="knowledge-rag-feedback-thanks" data-testid="rag-feedback-thanks">
                    <i className="fa-solid fa-circle-check text-emerald-600"></i>
                    <span>Thank you! Feedback recorded for RAG quality review.</span>
                  </span>
                ) : (
                  <div className="knowledge-rag-feedback-actions">
                    <button
                      type="button"
                      className={`knowledge-feedback-thumb-btn ${
                        ragFeedbackRating === 'helpful' ? 'active helpful' : ''
                      }`}
                      onClick={() => handleSelectFeedbackRating('helpful')}
                      disabled={submittingFeedback}
                      title="Thumbs up — Helpful"
                    >
                      <i className="fa-solid fa-thumbs-up"></i>
                      <span>Helpful</span>
                    </button>
                    <button
                      type="button"
                      className={`knowledge-feedback-thumb-btn ${
                        ragFeedbackRating === 'not_helpful' ? 'active not-helpful' : ''
                      }`}
                      onClick={() => handleSelectFeedbackRating('not_helpful')}
                      disabled={submittingFeedback}
                      title="Thumbs down — Not helpful"
                    >
                      <i className="fa-solid fa-thumbs-down"></i>
                      <span>Not Helpful</span>
                    </button>
                  </div>
                )}
              </div>

              {ragFeedbackRating === 'not_helpful' && !feedbackSubmitted && (
                <form
                  className="knowledge-rag-feedback-form"
                  onSubmit={handleSubmitNegativeFeedback}
                  data-testid="rag-negative-feedback-form"
                >
                  <div className="knowledge-rag-feedback-inputs">
                    <select
                      className="knowledge-select"
                      aria-label="Negative feedback reason"
                      value={ragFeedbackReason}
                      onChange={(e) => setRagFeedbackReason(e.target.value)}
                    >
                      <option value="incorrect_answer">Incorrect answer</option>
                      <option value="irrelevant_information">Irrelevant information</option>
                      <option value="outdated_information">Outdated information</option>
                      <option value="missing_information">Missing information</option>
                      <option value="incorrect_citation">Incorrect source or citation</option>
                      <option value="other">Other</option>
                    </select>
                    <input
                      type="text"
                      className="knowledge-form-input"
                      placeholder="Optional comment on what was inaccurate, missing, or outdated..."
                      value={ragFeedbackComment}
                      onChange={(e) => setRagFeedbackComment(e.target.value)}
                    />
                    <button
                      type="submit"
                      className="knowledge-btn knowledge-btn-primary"
                      disabled={submittingFeedback}
                    >
                      <i
                        className={`fa-solid ${
                          submittingFeedback ? 'fa-spinner fa-spin' : 'fa-paper-plane'
                        }`}
                      ></i>
                      <span>{submittingFeedback ? 'Sending...' : 'Submit Feedback'}</span>
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Navigation Tabs (Role-Aware) ── */}
      <div className="knowledge-tabs-bar">
        <button
          type="button"
          className={`knowledge-tab-btn ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <i className="fa-solid fa-folder-open"></i>
          All Active Sources
          <span className="knowledge-tab-count">{counts.all}</span>
        </button>

        {/* Pending Approval Tab (Visible for Reviewers and Sales Members) */}
        <button
          type="button"
          className={`knowledge-tab-btn ${activeTab === 'pending_approval' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending_approval')}
        >
          <i className="fa-solid fa-clock"></i>
          Pending Approval
          {counts.pending > 0 && (
            <span className="knowledge-tab-badge pending">{counts.pending}</span>
          )}
        </button>

        {/* Rejected Tab (Reviewer only) */}
        {isReviewer ? (
          <button
            type="button"
            className={`knowledge-tab-btn ${activeTab === 'rejected' ? 'active' : ''}`}
            onClick={() => setActiveTab('rejected')}
          >
            <i className="fa-solid fa-circle-xmark"></i>
            Rejected
            <span className="knowledge-tab-count">{counts.rejected}</span>
          </button>
        ) : null}

        {/* Quality & Evaluation Loop Tab (Reviewer only) */}
        {isReviewer ? (
          <button
            type="button"
            className={`knowledge-tab-btn ${activeTab === 'quality_loop' ? 'active' : ''}`}
            onClick={() => setActiveTab('quality_loop')}
          >
            <i className="fa-solid fa-chart-line"></i>
            Quality &amp; Evaluation
            {counts.attention > 0 && (
              <span className="knowledge-tab-badge pending">{counts.attention}</span>
            )}
          </button>
        ) : null}
      </div>

      {/* ── Toolbar / Search & Filter (shown for knowledge source tabs) ── */}
      {activeTab !== 'quality_loop' && (
        <div className="knowledge-toolbar">
          <div className="knowledge-search-wrap">
            <i className="fa-solid fa-magnifying-glass knowledge-search-icon"></i>
            <input
              type="text"
              className="knowledge-search-input"
              placeholder="Search knowledge sources, claims, or citations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="knowledge-filter-group">
            <select
              className="knowledge-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="documents">Documents</option>
              <option value="web">Web &amp; URLs</option>
            </select>
            <select
              className="knowledge-select"
              aria-label="Filter by knowledge type"
              value={knowledgeTypeFilter}
              onChange={(e) => setKnowledgeTypeFilter(e.target.value)}
            >
              <option value="all">All Knowledge Types</option>
              <option value="internal">Internal</option>
              <option value="external">External</option>
            </select>
            <select
              className="knowledge-select"
              aria-label="Filter by freshness status"
              value={freshnessFilter}
              onChange={(e) => setFreshnessFilter(e.target.value)}
            >
              <option value="all">All Freshness</option>
              <option value="current">Current</option>
              <option value="review_due">Review Due</option>
              <option value="expired">Expired</option>
              <option value="superseded">Superseded</option>
              <option value="pending_verification">Pending Verification</option>
            </select>
            <button
              type="button"
              className="knowledge-btn knowledge-btn-primary"
              onClick={() => setIsAddModalOpen(true)}
            >
              <i className="fa-solid fa-cloud-arrow-up"></i>
              Add Source
            </button>
          </div>
        </div>
      )}

      {/* ── Knowledge Assets or Quality Loop Content Area ── */}
      <div className="knowledge-content-container">
        {activeTab === 'quality_loop' && isReviewer ? (
          <div className="knowledge-quality-loop-panel" data-testid="quality-loop-panel">
            {/* ── Executive Quality Control Banner ── */}
            <div className="knowledge-quality-header-card">
              <div className="knowledge-quality-header-top">
                <div className="knowledge-quality-hero-left">
                  <div className="knowledge-quality-hero-icon">
                    <i className="fa-solid fa-shield-heart"></i>
                  </div>
                  <div className="knowledge-quality-hero-text">
                    <div className="knowledge-quality-title-row">
                      <h3 className="knowledge-quality-section-title">
                        Knowledge Freshness, RAG Quality Evaluation &amp; Continuous Loop
                      </h3>
                      <span
                        className={`knowledge-quality-gate-pill ${
                          !qualityDashboard?.latestEvaluation
                            ? 'not-run'
                            : qualityDashboard.latestEvaluation.regressionPassed !== false
                              ? 'passing'
                              : 'regression'
                        }`}
                      >
                        <i
                          className={`fa-solid ${
                            !qualityDashboard?.latestEvaluation
                              ? 'fa-clock'
                              : qualityDashboard.latestEvaluation.regressionPassed !== false
                                ? 'fa-circle-check'
                                : 'fa-triangle-exclamation'
                          }`}
                        ></i>
                        {!qualityDashboard?.latestEvaluation
                          ? 'Awaiting First Evaluation'
                          : qualityDashboard.latestEvaluation.regressionPassed !== false
                            ? 'Regression Gate: Passing'
                            : 'Regression Alert'}
                      </span>
                    </div>
                    <p className="knowledge-quality-section-desc">
                      Enforce document freshness &amp; expiration rules, measure retrieval and
                      grounded answer accuracy against workspace test cases, and triage user
                      feedback.
                    </p>
                  </div>
                </div>

                <div className="knowledge-quality-header-meta-panel">
                  <div className="knowledge-quality-meta-strip">
                    <span className="knowledge-quality-meta-chip">
                      <i className="fa-solid fa-microchip"></i>
                      Model:{' '}
                      <strong>
                        {qualityDashboard?.latestEvaluation?.modelVersion || 'MiniMax-M2.5'}
                      </strong>
                    </span>
                    {qualityDashboard?.latestEvaluation?.createdAt && (
                      <span className="knowledge-quality-meta-chip">
                        <i className="fa-regular fa-calendar-check"></i>
                        Last Run:{' '}
                        <strong>
                          {new Date(qualityDashboard.latestEvaluation.createdAt).toLocaleString()}
                        </strong>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="knowledge-quality-hero-actions">
                <button
                  type="button"
                  className="knowledge-btn knowledge-btn-secondary"
                  onClick={handleRunFreshnessSweep}
                  disabled={runningSweep}
                >
                  <i
                    className={`fa-solid ${
                      runningSweep ? 'fa-spinner fa-spin' : 'fa-clock-rotate-left'
                    }`}
                  ></i>
                  <span>{runningSweep ? 'Checking...' : 'Run Freshness Sweep'}</span>
                </button>
                <button
                  type="button"
                  className="knowledge-btn knowledge-btn-secondary"
                  onClick={() => setIsAddEvalCaseModalOpen(true)}
                >
                  <i className="fa-solid fa-plus"></i>
                  <span>Add Test Case</span>
                </button>
                <button
                  type="button"
                  className="knowledge-btn knowledge-btn-primary knowledge-quality-eval-cta"
                  onClick={handleRunEvaluation}
                  disabled={runningEval}
                >
                  <i
                    className={`fa-solid ${runningEval ? 'fa-spinner fa-spin' : 'fa-flask-vial'}`}
                  ></i>
                  <span>{runningEval ? 'Evaluating...' : 'Run RAG Evaluation Suite'}</span>
                </button>
              </div>
            </div>

            {/* ── 4-Column Executive Telemetry Grid ── */}
            <div className="knowledge-quality-metrics-grid">
              {/* Card 1: Freshness Health */}
              <div className="knowledge-quality-metric-card accent-emerald">
                <div className="knowledge-quality-card-head">
                  <div className="knowledge-quality-card-title-wrap">
                    <div className="knowledge-quality-card-icon emerald">
                      <i className="fa-solid fa-leaf"></i>
                    </div>
                    <div>
                      <span className="knowledge-quality-card-title">Freshness Status</span>
                      <span className="knowledge-quality-card-caption">
                        Lifecycle &amp; Expiration
                      </span>
                    </div>
                  </div>
                </div>

                <div className="knowledge-quality-hero-stat">
                  <div className="knowledge-quality-hero-number emerald">
                    {qualityDashboard?.freshness?.currentCount ??
                      stats.freshnessCounts?.current ??
                      0}
                  </div>
                  <div className="knowledge-quality-hero-label">
                    <span>Active &amp; Eligible Sources</span>
                    <small>Verified for production RAG</small>
                  </div>
                </div>

                <div className="knowledge-quality-stat-list">
                  <div className="knowledge-quality-stat-row">
                    <span className="knowledge-quality-stat-label">
                      <span className="knowledge-dot emerald"></span>
                      Current &amp; Eligible
                    </span>
                    <strong className="text-emerald-600">
                      {qualityDashboard?.freshness?.currentCount ??
                        stats.freshnessCounts?.current ??
                        0}
                    </strong>
                  </div>
                  <div className="knowledge-quality-stat-row">
                    <span className="knowledge-quality-stat-label">
                      <span className="knowledge-dot amber"></span>
                      Review Due
                    </span>
                    <strong className="text-amber-600">
                      {qualityDashboard?.freshness?.reviewDueCount ??
                        stats.freshnessCounts?.reviewDue ??
                        0}
                    </strong>
                  </div>
                  <div className="knowledge-quality-stat-row">
                    <span className="knowledge-quality-stat-label">
                      <span className="knowledge-dot rose"></span>
                      Expired (Excluded)
                    </span>
                    <strong className="text-rose-600">
                      {qualityDashboard?.freshness?.expiredCount ??
                        stats.freshnessCounts?.expired ??
                        0}
                    </strong>
                  </div>
                  <div className="knowledge-quality-stat-row">
                    <span className="knowledge-quality-stat-label">
                      <span className="knowledge-dot slate"></span>
                      Superseded (Excluded)
                    </span>
                    <strong className="text-slate-600">
                      {qualityDashboard?.freshness?.supersededCount ??
                        stats.freshnessCounts?.superseded ??
                        0}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Card 2: Retrieval Quality Metrics */}
              <div className="knowledge-quality-metric-card accent-blue">
                <div className="knowledge-quality-card-head">
                  <div className="knowledge-quality-card-title-wrap">
                    <div className="knowledge-quality-card-icon blue">
                      <i className="fa-solid fa-magnifying-glass-chart"></i>
                    </div>
                    <div>
                      <span className="knowledge-quality-card-title">Retrieval Metrics</span>
                      <span className="knowledge-quality-card-caption">
                        Top-K Hybrid Search Precision
                      </span>
                    </div>
                  </div>
                </div>

                <div className="knowledge-quality-hero-stat">
                  <div className="knowledge-quality-hero-number blue">
                    {qualityDashboard?.latestEvaluation
                      ? `${Math.round(
                          (qualityDashboard.latestEvaluation.retrievalMetrics?.recallAtK ?? 0) * 100
                        )}%`
                      : '—'}
                  </div>
                  <div className="knowledge-quality-hero-label">
                    <span>Recall@K Score</span>
                    <small>Target threshold: &ge; 75%</small>
                  </div>
                </div>

                <div className="knowledge-quality-progress-track">
                  <div
                    className="knowledge-quality-progress-fill blue"
                    style={{
                      width: qualityDashboard?.latestEvaluation
                        ? `${Math.min(
                            100,
                            Math.round(
                              (qualityDashboard.latestEvaluation.retrievalMetrics?.recallAtK ?? 0) *
                                100
                            )
                          )}%`
                        : '0%',
                    }}
                  />
                </div>

                <div className="knowledge-quality-stat-list">
                  <div className="knowledge-quality-stat-row">
                    <span>Recall@K</span>
                    <strong>
                      {qualityDashboard?.latestEvaluation
                        ? `${Math.round(
                            (qualityDashboard.latestEvaluation.retrievalMetrics?.recallAtK ?? 0) *
                              100
                          )}%`
                        : '—'}
                    </strong>
                  </div>
                  <div className="knowledge-quality-stat-row">
                    <span>Precision@K</span>
                    <strong>
                      {qualityDashboard?.latestEvaluation
                        ? `${Math.round(
                            (qualityDashboard.latestEvaluation.retrievalMetrics?.precisionAtK ??
                              0) * 100
                          )}%`
                        : '—'}
                    </strong>
                  </div>
                  <div className="knowledge-quality-stat-row">
                    <span>Mean Reciprocal Rank (MRR)</span>
                    <strong>
                      {qualityDashboard?.latestEvaluation
                        ? (qualityDashboard.latestEvaluation.retrievalMetrics?.mrr ?? 0).toFixed(2)
                        : '—'}
                    </strong>
                  </div>
                  <div className="knowledge-quality-stat-row">
                    <span>Stale/Expired Leakage</span>
                    <strong className="text-emerald-600">
                      {qualityDashboard?.latestEvaluation
                        ? `${Math.round(
                            (qualityDashboard.latestEvaluation.retrievalMetrics
                              ?.expiredOrSupersededLeakageRate ?? 0) * 100
                          )}%`
                        : '—'}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Card 3: Grounded Answer Metrics */}
              <div className="knowledge-quality-metric-card accent-indigo">
                <div className="knowledge-quality-card-head">
                  <div className="knowledge-quality-card-title-wrap">
                    <div className="knowledge-quality-card-icon indigo">
                      <i className="fa-solid fa-wand-magic-sparkles"></i>
                    </div>
                    <div>
                      <span className="knowledge-quality-card-title">Answer Metrics</span>
                      <span className="knowledge-quality-card-caption">
                        Groundedness &amp; Citations
                      </span>
                    </div>
                  </div>
                </div>

                <div className="knowledge-quality-hero-stat">
                  <div className="knowledge-quality-hero-number indigo">
                    {qualityDashboard?.latestEvaluation
                      ? `${Math.round(
                          (qualityDashboard.latestEvaluation.answerMetrics?.faithfulness ?? 0) * 100
                        )}%`
                      : '—'}
                  </div>
                  <div className="knowledge-quality-hero-label">
                    <span>Grounded Faithfulness</span>
                    <small>Target threshold: &ge; 80%</small>
                  </div>
                </div>

                <div className="knowledge-quality-progress-track">
                  <div
                    className="knowledge-quality-progress-fill indigo"
                    style={{
                      width: qualityDashboard?.latestEvaluation
                        ? `${Math.min(
                            100,
                            Math.round(
                              (qualityDashboard.latestEvaluation.answerMetrics?.faithfulness ?? 0) *
                                100
                            )
                          )}%`
                        : '0%',
                    }}
                  />
                </div>

                <div className="knowledge-quality-stat-list">
                  <div className="knowledge-quality-stat-row">
                    <span>Faithfulness / Groundedness</span>
                    <strong>
                      {qualityDashboard?.latestEvaluation
                        ? `${Math.round(
                            (qualityDashboard.latestEvaluation.answerMetrics?.faithfulness ?? 0) *
                              100
                          )}%`
                        : '—'}
                    </strong>
                  </div>
                  <div className="knowledge-quality-stat-row">
                    <span>Answer Relevance</span>
                    <strong>
                      {qualityDashboard?.latestEvaluation
                        ? `${Math.round(
                            (qualityDashboard.latestEvaluation.answerMetrics?.answerRelevance ??
                              0) * 100
                          )}%`
                        : '—'}
                    </strong>
                  </div>
                  <div className="knowledge-quality-stat-row">
                    <span>Citation Accuracy</span>
                    <strong>
                      {qualityDashboard?.latestEvaluation
                        ? `${Math.round(
                            (qualityDashboard.latestEvaluation.answerMetrics?.citationAccuracy ??
                              0) * 100
                          )}%`
                        : '—'}
                    </strong>
                  </div>
                  <div className="knowledge-quality-stat-row">
                    <span>Abstention Quality</span>
                    <strong>
                      {qualityDashboard?.latestEvaluation
                        ? `${Math.round(
                            (qualityDashboard.latestEvaluation.answerMetrics?.abstentionQuality ??
                              0) * 100
                          )}%`
                        : '—'}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Card 4: User Feedback & Regression Gate */}
              <div className="knowledge-quality-metric-card accent-purple">
                <div className="knowledge-quality-card-head">
                  <div className="knowledge-quality-card-title-wrap">
                    <div className="knowledge-quality-card-icon purple">
                      <i className="fa-solid fa-rotate"></i>
                    </div>
                    <div>
                      <span className="knowledge-quality-card-title">Continuous Quality Loop</span>
                      <span className="knowledge-quality-card-caption">
                        Feedback &amp; Regression Gate
                      </span>
                    </div>
                  </div>
                </div>

                <div className="knowledge-quality-hero-stat">
                  <div className="knowledge-quality-hero-number purple">
                    {evalCases.length || qualityDashboard?.evalCaseCount || 0}
                  </div>
                  <div className="knowledge-quality-hero-label">
                    <span>Active Benchmark Cases</span>
                    <small>
                      {qualityDashboard?.latestEvaluation
                        ? `Last run: ${qualityDashboard.latestEvaluation.passedCases ?? 0}/${
                            qualityDashboard.latestEvaluation.totalCases ?? 0
                          } passed`
                        : 'Ready for evaluation run'}
                    </small>
                  </div>
                </div>

                <div className="knowledge-quality-stat-list">
                  <div className="knowledge-quality-stat-row">
                    <span className="knowledge-quality-stat-label">
                      <span className="knowledge-dot emerald"></span>
                      Helpful Responses
                    </span>
                    <strong className="text-emerald-600">
                      {qualityDashboard?.feedbackSummary?.helpfulCount ?? 0}
                    </strong>
                  </div>
                  <div className="knowledge-quality-stat-row">
                    <span className="knowledge-quality-stat-label">
                      <span className="knowledge-dot rose"></span>
                      Negative Feedback
                    </span>
                    <strong className="text-rose-600">
                      {qualityDashboard?.feedbackSummary?.notHelpfulCount ?? 0}
                    </strong>
                  </div>
                  <div className="knowledge-quality-stat-row">
                    <span className="knowledge-quality-stat-label">
                      <span className="knowledge-dot amber"></span>
                      Open Action Items
                    </span>
                    <strong className="text-amber-600">
                      {(qualityDashboard?.feedbackSummary?.newCount ?? 0) +
                        (qualityDashboard?.feedbackSummary?.actionRequiredCount ?? 0)}
                    </strong>
                  </div>
                  <div className="knowledge-quality-stat-row">
                    <span className="knowledge-quality-stat-label">
                      <span className="knowledge-dot slate"></span>
                      Active Eval Cases
                    </span>
                    <strong>{evalCases.length || qualityDashboard?.evalCaseCount || 0}</strong>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Latest Evaluation Run Results (Module C3 & D2) ── */}
            {qualityDashboard?.latestEvaluation && (
              <div
                className="knowledge-quality-table-card knowledge-eval-single-card"
                data-testid="latest-eval-result-slip"
              >
                <div className="knowledge-eval-single-content">
                  <div className="knowledge-quality-section-badge indigo">
                    <i className="fa-solid fa-file-pdf"></i>
                  </div>
                  <div className="knowledge-eval-single-details">
                    <div className="knowledge-quality-title-inline">
                      <h4 className="knowledge-quality-subtitle">Latest Evaluation Run Results</h4>
                      <span
                        className={`knowledge-badge ${
                          (qualityDashboard.latestEvaluation.passedCases ?? 0) ===
                          (qualityDashboard.latestEvaluation.totalCases ?? 0)
                            ? 'approved'
                            : 'pending_approval'
                        }`}
                      >
                        {qualityDashboard.latestEvaluation.passedCases ?? 0} /{' '}
                        {qualityDashboard.latestEvaluation.totalCases ?? 0} Passed
                      </span>
                    </div>
                    <p className="knowledge-quality-subtext">
                      Per-question retrieval accuracy, grounded answer preview, and citation
                      verification packaged as an exportable PDF report.
                    </p>
                    <div className="knowledge-eval-meta-chips">
                      <span className="knowledge-eval-chip">
                        <i className="fa-regular fa-clock"></i>
                        {qualityDashboard.latestEvaluation.createdAt
                          ? new Date(qualityDashboard.latestEvaluation.createdAt).toLocaleString()
                          : 'Recent Run'}
                      </span>
                      <span className="knowledge-eval-chip">
                        <i className="fa-solid fa-layer-group"></i>
                        Dataset{' '}
                        {String(
                          qualityDashboard.latestEvaluation.datasetVersion || '1.0'
                        ).startsWith('v')
                          ? qualityDashboard.latestEvaluation.datasetVersion
                          : `v${qualityDashboard.latestEvaluation.datasetVersion || '1.0'}`}
                      </span>
                      <span className="knowledge-eval-chip">
                        <i className="fa-solid fa-microchip"></i>
                        {qualityDashboard.latestEvaluation.modelVersion || 'MiniMax-M2.5'}
                      </span>
                      <span className="knowledge-eval-chip">
                        <i className="fa-solid fa-bullseye"></i>
                        Recall@K:{' '}
                        {Math.round(
                          (qualityDashboard.latestEvaluation.retrievalMetrics?.recallAtK ?? 0) * 100
                        )}
                        %
                      </span>
                      <span className="knowledge-eval-chip">
                        <i className="fa-solid fa-shield-check"></i>
                        Faithfulness:{' '}
                        {Math.round(
                          (qualityDashboard.latestEvaluation.answerMetrics?.faithfulness ?? 0) * 100
                        )}
                        %
                      </span>
                      <span className="knowledge-eval-chip">
                        <i className="fa-solid fa-quote-right"></i>
                        Citation Acc:{' '}
                        {Math.round(
                          (qualityDashboard.latestEvaluation.answerMetrics?.citationAccuracy ?? 0) *
                            100
                        )}
                        %
                      </span>
                    </div>
                  </div>
                  <div className="knowledge-eval-single-action">
                    <button
                      type="button"
                      className="knowledge-btn knowledge-btn-primary"
                      onClick={handleDownloadEvalReport}
                      disabled={downloadingEvalPdf}
                      title="Download Evaluation Report in PDF format"
                      data-testid="download-eval-pdf-main-btn"
                    >
                      <i
                        className={`fa-solid ${
                          downloadingEvalPdf ? 'fa-spinner fa-spin' : 'fa-file-arrow-down'
                        }`}
                      ></i>
                      <span>
                        {downloadingEvalPdf ? 'Generating PDF...' : 'Download Report (PDF)'}
                      </span>
                    </button>
                    <span className="knowledge-eval-file-info">
                      Format: Vector PDF · Ready for audit
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* ── Versioned Evaluation Dataset Table (Module C1 & C3) ── */}
            <div className="knowledge-quality-table-card">
              <div className="knowledge-quality-table-header">
                <div className="knowledge-quality-section-head-left">
                  <div className="knowledge-quality-section-badge blue">
                    <i className="fa-solid fa-vials"></i>
                  </div>
                  <div>
                    <div className="knowledge-quality-title-inline">
                      <h4 className="knowledge-quality-subtitle">
                        Versioned RAG Evaluation Dataset
                      </h4>
                      <span className="knowledge-badge type">
                        {evalCases.length} {evalCases.length === 1 ? 'Case' : 'Cases'}
                      </span>
                    </div>
                    <p className="knowledge-quality-subtext">
                      Define workspace-specific ground-truth questions or derive test cases directly
                      from your approved knowledge sources.
                    </p>
                  </div>
                </div>
                <div className="knowledge-filter-group">
                  <button
                    type="button"
                    className="knowledge-btn knowledge-btn-secondary"
                    onClick={() => setIsAddEvalCaseModalOpen(true)}
                  >
                    <i className="fa-solid fa-plus"></i>
                    <span>Add Evaluation Case</span>
                  </button>
                </div>
              </div>
              {evalCases.length === 0 ? (
                <div className="knowledge-quality-empty-box">
                  <div className="knowledge-quality-empty-icon">
                    <i className="fa-solid fa-flask-vial"></i>
                  </div>
                  <h5>No Evaluation Test Cases Defined Yet</h5>
                  <p>
                    Create custom ground-truth questions linked to your workspace documents, or run
                    the evaluation suite to automatically build test cases from your approved
                    knowledge sources.
                  </p>
                  <div className="knowledge-quality-empty-actions">
                    <button
                      type="button"
                      className="knowledge-btn knowledge-btn-secondary"
                      onClick={() => setIsAddEvalCaseModalOpen(true)}
                    >
                      <i className="fa-solid fa-plus"></i>
                      <span>Add Custom Test Case</span>
                    </button>
                    <button
                      type="button"
                      className="knowledge-btn knowledge-btn-primary"
                      onClick={handleRunEvaluation}
                      disabled={runningEval}
                    >
                      <i
                        className={`fa-solid ${
                          runningEval ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'
                        }`}
                      ></i>
                      <span>
                        {runningEval
                          ? 'Generating & Evaluating...'
                          : 'Generate from Knowledge & Run'}
                      </span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="knowledge-table-wrap knowledge-quality-scroll-wrap">
                  <table className="knowledge-quality-table">
                    <thead>
                      <tr>
                        <th style={{ width: '180px' }}>Category</th>
                        <th>Test Question &amp; Reference</th>
                        <th style={{ width: '160px' }}>Expected Behavior</th>
                        <th style={{ width: '200px' }}>Required Keywords</th>
                        <th style={{ width: '100px', textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {evalCases.map((ec) => (
                        <tr key={ec.id}>
                          <td>
                            <span className="knowledge-badge type">
                              {(ec.eval_category || ec.category || 'general').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td>
                            <div className="knowledge-eval-question-text">{ec.question}</div>
                            {(ec.expected_answer || ec.reference_answer) && (
                              <div className="knowledge-eval-answer-preview">
                                {ec.expected_answer || ec.reference_answer}
                              </div>
                            )}
                          </td>
                          <td>
                            <span
                              className={`knowledge-badge ${
                                ec.expected_behavior === 'abstain' ||
                                ec.expected_behavior === 'exclude_expired'
                                  ? 'pending_approval'
                                  : 'approved'
                              }`}
                            >
                              <i
                                className={`fa-solid ${
                                  ec.expected_behavior === 'abstain' ||
                                  ec.expected_behavior === 'exclude_expired'
                                    ? 'fa-shield-halved'
                                    : 'fa-check-double'
                                }`}
                              ></i>
                              {ec.expected_behavior === 'abstain' ||
                              ec.expected_behavior === 'exclude_expired'
                                ? 'Abstain Safely'
                                : 'Grounded Answer'}
                            </span>
                          </td>
                          <td>
                            {Array.isArray(ec.expected_keywords) &&
                            ec.expected_keywords.length > 0 ? (
                              <div className="knowledge-eval-keyword-list">
                                {ec.expected_keywords.map((kw, kIdx) => (
                                  <span key={kIdx} className="knowledge-eval-keyword-tag">
                                    {kw}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td>
                            <div
                              className="knowledge-eval-actions"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.375rem',
                              }}
                            >
                              <button
                                type="button"
                                className="knowledge-icon-btn modify-case"
                                title="Update Test Case"
                                aria-label="Update test case"
                                onClick={() => handleOpenModifyEvalCase(ec)}
                              >
                                <i className="fa-solid fa-pen-to-square"></i>
                              </button>
                              <button
                                type="button"
                                className="knowledge-icon-btn danger remove-case"
                                title="Remove Test Case"
                                aria-label="Remove test case"
                                onClick={() => handleDeleteEvalCase(ec)}
                                disabled={deletingCaseId === ec.id}
                              >
                                <i
                                  className={`fa-solid ${
                                    deletingCaseId === ec.id ? 'fa-spinner fa-spin' : 'fa-trash-can'
                                  }`}
                                ></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── User Feedback Review Table (Module B3 & D1) ── */}
            <div className="knowledge-quality-table-card">
              <div className="knowledge-quality-table-header">
                <div className="knowledge-quality-section-head-left">
                  <div className="knowledge-quality-section-badge purple">
                    <i className="fa-solid fa-comments"></i>
                  </div>
                  <div>
                    <div className="knowledge-quality-title-inline">
                      <h4 className="knowledge-quality-subtitle">
                        User Feedback Triage &amp; Corrective Actions
                      </h4>
                      <span className="knowledge-badge type">
                        {feedbackItems.length} {feedbackItems.length === 1 ? 'Entry' : 'Entries'}
                      </span>
                    </div>
                    <p className="knowledge-quality-subtext">
                      Audit Grounded Q&amp;A response ratings, investigate negative feedback root
                      causes, and execute verified corrective resolutions across knowledge sources.
                    </p>
                  </div>
                </div>

                {(fbFilterRating !== 'all' ||
                  fbFilterStatus !== 'all' ||
                  fbFilterReason !== 'all' ||
                  fbSearchQuery) && (
                  <button
                    type="button"
                    className="knowledge-btn knowledge-btn-secondary"
                    style={{ fontSize: '0.75rem', padding: '0.35rem 0.65rem' }}
                    onClick={() => {
                      setFbFilterRating('all');
                      setFbFilterStatus('all');
                      setFbFilterReason('all');
                      setFbSearchQuery('');
                    }}
                  >
                    <i className="fa-solid fa-arrow-rotate-left mr-1"></i> Reset Filters
                  </button>
                )}
              </div>

              {/* ── Interactive KPI Triage Counter Bar ── */}
              <div className="knowledge-feedback-stat-strip">
                <div
                  className={`knowledge-feedback-stat-card ${
                    fbFilterRating === 'all' && fbFilterStatus === 'all' ? 'active' : ''
                  }`}
                  onClick={() => {
                    setFbFilterRating('all');
                    setFbFilterStatus('all');
                  }}
                  title="Click to view all feedback entries"
                >
                  <div className="knowledge-feedback-stat-icon blue">
                    <i className="fa-solid fa-comments"></i>
                  </div>
                  <div className="knowledge-feedback-stat-meta">
                    <span className="knowledge-feedback-stat-label">Total Feedback</span>
                    <span className="knowledge-feedback-stat-value">{feedbackMetrics.total}</span>
                    <span className="knowledge-feedback-stat-sub">
                      All Grounded Q&amp;A ratings
                    </span>
                  </div>
                </div>

                <div
                  className={`knowledge-feedback-stat-card ${
                    fbFilterRating === 'helpful' ? 'active' : ''
                  }`}
                  onClick={() => {
                    setFbFilterRating('helpful');
                    setFbFilterStatus('all');
                  }}
                  title="Click to view helpful positive ratings"
                >
                  <div className="knowledge-feedback-stat-icon emerald">
                    <i className="fa-solid fa-thumbs-up"></i>
                  </div>
                  <div className="knowledge-feedback-stat-meta">
                    <span className="knowledge-feedback-stat-label">Helpful Answers</span>
                    <span className="knowledge-feedback-stat-value">
                      {feedbackMetrics.helpful}{' '}
                      <small className="text-xs font-semibold text-emerald-600">
                        ({feedbackMetrics.helpfulPct}%)
                      </small>
                    </span>
                    <span className="knowledge-feedback-stat-sub">No action needed</span>
                  </div>
                </div>

                <div
                  className={`knowledge-feedback-stat-card ${
                    fbFilterRating === 'not_helpful' && fbFilterStatus !== 'resolved'
                      ? 'active'
                      : ''
                  }`}
                  onClick={() => {
                    setFbFilterRating('not_helpful');
                    setFbFilterStatus('all');
                  }}
                  title="Click to filter unresolved negative feedback"
                >
                  <div className="knowledge-feedback-stat-icon rose">
                    <i className="fa-solid fa-triangle-exclamation"></i>
                  </div>
                  <div className="knowledge-feedback-stat-meta">
                    <span className="knowledge-feedback-stat-label">Open Triage Items</span>
                    <span className="knowledge-feedback-stat-value text-rose-600">
                      {feedbackMetrics.openAction}
                    </span>
                    <span className="knowledge-feedback-stat-sub">Requires root-cause triage</span>
                  </div>
                </div>

                <div
                  className={`knowledge-feedback-stat-card ${
                    fbFilterStatus === 'resolved' ? 'active' : ''
                  }`}
                  onClick={() => {
                    setFbFilterRating('all');
                    setFbFilterStatus('resolved');
                  }}
                  title="Click to view resolved feedback items"
                >
                  <div className="knowledge-feedback-stat-icon indigo">
                    <i className="fa-solid fa-circle-check"></i>
                  </div>
                  <div className="knowledge-feedback-stat-meta">
                    <span className="knowledge-feedback-stat-label">Resolved Issues</span>
                    <span className="knowledge-feedback-stat-value text-indigo-600">
                      {feedbackMetrics.resolved}
                    </span>
                    <span className="knowledge-feedback-stat-sub">Corrective action verified</span>
                  </div>
                </div>
              </div>

              {/* ── Toolbar & Filters ── */}
              <div className="knowledge-feedback-toolbar">
                <div className="knowledge-feedback-search">
                  <i className="fa-solid fa-magnifying-glass knowledge-feedback-search-icon"></i>
                  <input
                    type="text"
                    className="knowledge-feedback-search-input"
                    placeholder="Search question, comments, or resolution..."
                    value={fbSearchQuery}
                    onChange={(e) => setFbSearchQuery(e.target.value)}
                  />
                  {fbSearchQuery && (
                    <button
                      type="button"
                      className="knowledge-feedback-clear-search"
                      onClick={() => setFbSearchQuery('')}
                      aria-label="Clear search"
                    >
                      &times;
                    </button>
                  )}
                </div>

                <div className="knowledge-filter-group">
                  <select
                    className="knowledge-select"
                    aria-label="Filter by rating"
                    value={fbFilterRating}
                    onChange={(e) => setFbFilterRating(e.target.value)}
                  >
                    <option value="all">All Ratings</option>
                    <option value="not_helpful">Not Helpful (Negative)</option>
                    <option value="helpful">Helpful (Positive)</option>
                  </select>
                  <select
                    className="knowledge-select"
                    aria-label="Filter by lifecycle status"
                    value={fbFilterStatus}
                    onChange={(e) => setFbFilterStatus(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="new">New</option>
                    <option value="under_review">Under Review</option>
                    <option value="action_required">Action Required</option>
                    <option value="resolved">Resolved</option>
                  </select>
                  <select
                    className="knowledge-select"
                    aria-label="Filter by root cause reason"
                    value={fbFilterReason}
                    onChange={(e) => setFbFilterReason(e.target.value)}
                  >
                    <option value="all">All Reasons</option>
                    <option value="incorrect_answer">Incorrect Answer</option>
                    <option value="outdated_information">Outdated Info</option>
                    <option value="missing_information">Missing Info</option>
                    <option value="irrelevant_information">Irrelevant Info</option>
                    <option value="incorrect_citation">Incorrect Citation</option>
                  </select>

                  <div className="knowledge-view-mode-toggle">
                    <button
                      type="button"
                      className={`knowledge-view-mode-btn ${fbViewMode === 'cards' ? 'active' : ''}`}
                      onClick={() => setFbViewMode('cards')}
                      title="Modern Triage Card Feed"
                    >
                      <i className="fa-solid fa-grip-vertical mr-1"></i> Cards
                    </button>
                    <button
                      type="button"
                      className={`knowledge-view-mode-btn ${fbViewMode === 'table' ? 'active' : ''}`}
                      onClick={() => setFbViewMode('table')}
                      title="Compact Table View"
                    >
                      <i className="fa-solid fa-list mr-1"></i> Table
                    </button>
                  </div>
                </div>
              </div>

              {qualityLoading ? (
                <div className="knowledge-loading-state py-8">
                  <div className="knowledge-spinner"></div>
                  <span>Loading quality feedback &amp; triage ledger...</span>
                </div>
              ) : displayedFeedbackItems.length === 0 ? (
                <div className="knowledge-quality-empty-box compact">
                  <div className="knowledge-quality-empty-icon purple">
                    <i className="fa-regular fa-comment-dots"></i>
                  </div>
                  <h5>No User Feedback Entries Found</h5>
                  <p>
                    {fbSearchQuery
                      ? `No feedback entries matched "${fbSearchQuery}". Try clearing your search query.`
                      : 'No feedback items match the selected filter combination. Grounded Q&A ratings appear here automatically.'}
                  </p>
                </div>
              ) : fbViewMode === 'cards' ? (
                <div className="knowledge-feedback-cards-grid">
                  {displayedFeedbackItems.map((fb) => (
                    <div
                      key={fb.id}
                      className={`knowledge-feedback-row knowledge-feedback-card ${
                        fb.rating === 'helpful' ? 'is-helpful' : 'is-negative'
                      } ${fb.status === 'resolved' ? 'is-resolved' : ''}`}
                    >
                      {/* Top Bar: Rating, Cause, Status, Date & Action */}
                      <div className="knowledge-feedback-card-header">
                        <div className="knowledge-feedback-card-badges">
                          <span
                            className={`knowledge-badge ${
                              fb.rating === 'helpful' ? 'approved' : 'rejected'
                            }`}
                          >
                            <i
                              className={`fa-solid ${
                                fb.rating === 'helpful' ? 'fa-thumbs-up' : 'fa-thumbs-down'
                              } mr-1`}
                            ></i>
                            {fb.rating === 'helpful' ? 'Helpful' : 'Not Helpful'}
                          </span>

                          {fb.rating !== 'helpful' && renderReasonBadge(fb.reason)}

                          {fb.rating === 'helpful' ? (
                            <span className="knowledge-badge approved">
                              <i className="fa-solid fa-check mr-1"></i> Satisfied
                            </span>
                          ) : (
                            <span className={`knowledge-badge status-${fb.status || 'new'}`}>
                              {(fb.status || 'new').replace(/_/g, ' ').toUpperCase()}
                            </span>
                          )}

                          <time className="knowledge-feedback-timestamp">
                            <i className="fa-regular fa-clock mr-1"></i>
                            {formatFeedbackDate(fb.created_at)}
                          </time>
                        </div>

                        <div className="knowledge-feedback-card-action">
                          {fb.rating === 'helpful' ? (
                            <span className="knowledge-feedback-satisfied-pill">
                              <i className="fa-solid fa-check mr-1.5"></i>
                              <span>No action needed</span>
                            </span>
                          ) : (
                            <button
                              type="button"
                              className={`knowledge-btn ${
                                fb.status === 'resolved'
                                  ? 'knowledge-btn-secondary'
                                  : 'knowledge-btn-primary'
                              } knowledge-triage-btn`}
                              onClick={() => {
                                setResolvingFeedbackItem(fb);
                                setResolveStatusInput(
                                  fb.status === 'new' ? 'resolved' : fb.status || 'resolved'
                                );
                                setResolveActionInput(fb.corrective_action || 'verified_knowledge');
                                setResolveNotesInput(fb.resolution_notes || '');
                                setResolvePromoteEval(false);
                              }}
                            >
                              <i
                                className={`fa-solid ${
                                  fb.status === 'resolved' ? 'fa-check-double' : 'fa-wrench'
                                } mr-1.5`}
                              ></i>
                              {fb.status === 'resolved' ? 'Edit Resolution' : 'Triage & Resolve'}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Card Body: Question & User Quote */}
                      <div className="knowledge-feedback-card-body">
                        <div className="knowledge-feedback-question-row">
                          <span className="knowledge-feedback-q-icon">Q</span>
                          <h5 className="knowledge-feedback-question-text">{fb.question}</h5>
                        </div>

                        {fb.comment && (
                          <div className="knowledge-feedback-user-quote">
                            <i className="fa-solid fa-quote-left text-indigo-500"></i>
                            <div className="knowledge-feedback-quote-body">
                              <span className="knowledge-feedback-quote-label">
                                User feedback comment:
                              </span>
                              <span className="knowledge-feedback-quote-text">
                                &ldquo;{fb.comment}&rdquo;
                              </span>
                            </div>
                          </div>
                        )}

                        {fb.resolution_notes && (
                          <div className="knowledge-feedback-resolution-pill">
                            <i className="fa-solid fa-shield-check text-emerald-600"></i>
                            <div>
                              <strong>
                                Resolution ({formatCorrectiveAction(fb.corrective_action)}):
                              </strong>{' '}
                              {fb.resolution_notes}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Card Footer: Source & Versions */}
                      <div className="knowledge-feedback-card-footer">
                        <div className="knowledge-feedback-footer-left">
                          {fb.linked_source_title ? (
                            <span
                              className="knowledge-feedback-source-pill"
                              title={fb.linked_source_title}
                            >
                              <i className="fa-regular fa-file-lines text-slate-400 mr-1.5"></i>
                              <span>
                                Source: <strong>{fb.linked_source_title}</strong>
                              </span>
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400 italic">
                              General Knowledge Query
                            </span>
                          )}
                        </div>
                        <div className="knowledge-feedback-footer-right">
                          <span
                            className="knowledge-version-chip model"
                            title={`Model: ${fb.model_version || 'Default'}`}
                          >
                            <i className="fa-solid fa-microchip"></i>
                            {cleanVersion(fb.model_version)}
                          </span>
                          <span
                            className="knowledge-version-chip prompt"
                            title={`Prompt: ${fb.prompt_version || 'Default'}`}
                          >
                            <i className="fa-solid fa-code"></i>
                            {cleanVersion(fb.prompt_version)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="knowledge-table-wrap knowledge-quality-scroll-wrap">
                  <table className="knowledge-quality-table knowledge-feedback-table">
                    <thead>
                      <tr>
                        <th style={{ width: '160px' }}>Signal &amp; Status</th>
                        <th>Question &amp; Feedback</th>
                        <th style={{ width: '170px' }}>Root Cause</th>
                        <th style={{ width: '160px', textAlign: 'center' }}>Corrective Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedFeedbackItems.map((fb) => (
                        <tr
                          key={fb.id}
                          className={`knowledge-feedback-row ${
                            fb.rating === 'helpful' ? 'is-helpful' : 'is-negative'
                          } ${fb.status === 'resolved' ? 'is-resolved' : ''}`}
                        >
                          <td>
                            <div className="knowledge-feedback-rating-wrap">
                              <span
                                className={`knowledge-badge ${
                                  fb.rating === 'helpful' ? 'approved' : 'rejected'
                                }`}
                              >
                                <i
                                  className={`fa-solid ${
                                    fb.rating === 'helpful' ? 'fa-thumbs-up' : 'fa-thumbs-down'
                                  } mr-1`}
                                ></i>
                                {fb.rating === 'helpful' ? 'Helpful' : 'Not Helpful'}
                              </span>

                              {fb.rating === 'helpful' ? (
                                <span className="knowledge-badge approved">
                                  <i className="fa-solid fa-check mr-1"></i> Satisfied
                                </span>
                              ) : (
                                <span className={`knowledge-badge status-${fb.status || 'new'}`}>
                                  {(fb.status || 'new').replace(/_/g, ' ').toUpperCase()}
                                </span>
                              )}

                              <time className="knowledge-feedback-timestamp">
                                <i className="fa-regular fa-clock mr-1"></i>
                                {formatFeedbackDate(fb.created_at)}
                              </time>
                            </div>
                          </td>
                          <td>
                            <div className="knowledge-eval-question-text">{fb.question}</div>
                            {fb.comment && (
                              <div className="knowledge-feedback-user-quote">
                                <i className="fa-solid fa-quote-left text-indigo-500"></i>
                                <div className="knowledge-feedback-quote-body">
                                  <span className="knowledge-feedback-quote-label">
                                    User feedback comment:
                                  </span>
                                  <span className="knowledge-feedback-quote-text">
                                    &ldquo;{fb.comment}&rdquo;
                                  </span>
                                </div>
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              {fb.linked_source_title && (
                                <span
                                  className="knowledge-feedback-source-pill"
                                  title={fb.linked_source_title}
                                >
                                  <i className="fa-regular fa-file-lines text-slate-400 mr-1.5"></i>
                                  <span>
                                    Source: <strong>{fb.linked_source_title}</strong>
                                  </span>
                                </span>
                              )}
                              <span
                                className="knowledge-version-chip model"
                                title={`Model: ${fb.model_version || 'Default'}`}
                              >
                                <i className="fa-solid fa-microchip"></i>
                                {cleanVersion(fb.model_version)}
                              </span>
                              <span
                                className="knowledge-version-chip prompt"
                                title={`Prompt: ${fb.prompt_version || 'Default'}`}
                              >
                                <i className="fa-solid fa-code"></i>
                                {cleanVersion(fb.prompt_version)}
                              </span>
                            </div>
                            {fb.resolution_notes && (
                              <div className="knowledge-feedback-resolution-pill mt-2">
                                <i className="fa-solid fa-shield-check text-emerald-600"></i>
                                <div>
                                  <strong>
                                    Resolution ({formatCorrectiveAction(fb.corrective_action)}):
                                  </strong>{' '}
                                  {fb.resolution_notes}
                                </div>
                              </div>
                            )}
                          </td>
                          <td>
                            {fb.rating === 'helpful' ? (
                              <span className="knowledge-feedback-na-text">
                                — Positive Grounding —
                              </span>
                            ) : (
                              renderReasonBadge(fb.reason)
                            )}
                          </td>
                          <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            {fb.rating === 'helpful' ? (
                              <span className="knowledge-feedback-satisfied-pill">
                                <i className="fa-solid fa-check mr-1.5"></i>
                                <span>No action needed</span>
                              </span>
                            ) : (
                              <button
                                type="button"
                                className={`knowledge-btn ${
                                  fb.status === 'resolved'
                                    ? 'knowledge-btn-secondary'
                                    : 'knowledge-btn-primary'
                                } knowledge-triage-btn`}
                                onClick={() => {
                                  setResolvingFeedbackItem(fb);
                                  setResolveStatusInput(
                                    fb.status === 'new' ? 'resolved' : fb.status || 'resolved'
                                  );
                                  setResolveActionInput(
                                    fb.corrective_action || 'verified_knowledge'
                                  );
                                  setResolveNotesInput(fb.resolution_notes || '');
                                  setResolvePromoteEval(false);
                                }}
                              >
                                <i
                                  className={`fa-solid ${
                                    fb.status === 'resolved' ? 'fa-check-double' : 'fa-wrench'
                                  } mr-1.5`}
                                ></i>
                                {fb.status === 'resolved' ? 'Edit Resolution' : 'Triage & Resolve'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : initialLoading && loading ? (
          <div className="knowledge-loading-state">
            <div className="knowledge-spinner"></div>
            <span>Loading knowledge assets...</span>
          </div>
        ) : displayedSources.length > 0 ? (
          /* ── Content Grid / List ── */
          <div
            className="knowledge-card-grid"
            style={{
              opacity: loading ? 0.6 : 1,
              pointerEvents: loading ? 'none' : 'auto',
            }}
          >
            {displayedSources.map((item) => {
              const chunksCount = item.chunks_count ?? item.chunksCount ?? 0;
              const sourceUrl = item.source_url || item.sourceUrl;
              const updatedAt = item.updated_at || item.updatedAt;
              const approvalStatus = item.approval_status || 'pending_approval';
              const freshnessStatus =
                item.freshness_status ||
                (approvalStatus === 'approved' ? 'current' : 'pending_verification');
              const format = item.format || 'MARKDOWN';
              const hasStoredFile = !!(
                item.storage_key ||
                (item.original_filename &&
                  item.source_type !== 'web' &&
                  item.source_type !== 'text' &&
                  item.format !== 'URL' &&
                  item.format !== 'WEB')
              );
              const isCreator =
                (item.created_by_user_id && item.created_by_user_id === userId) ||
                (item.created_by && item.created_by === username);
              const canDelete = isReviewer || isCreator;

              const freshnessLabelMap = {
                current: 'CURRENT',
                review_due: 'REVIEW DUE',
                expired: 'EXPIRED',
                superseded: 'SUPERSEDED',
                pending_verification: 'PENDING VERIFICATION',
              };

              return (
                <div key={item.id} className="knowledge-card">
                  <div>
                    <div className="knowledge-card-top">
                      <div className="knowledge-card-type-icon">
                        <i className={`fa-solid ${getFormatIcon(format)}`}></i>
                      </div>

                      <div className="knowledge-card-badges-right">
                        {approvalStatus === 'approved' && (
                          <span
                            className={`knowledge-badge freshness-${freshnessStatus}`}
                            title={`Freshness status: ${freshnessLabelMap[freshnessStatus] || freshnessStatus}`}
                          >
                            <i
                              className={`fa-solid ${
                                freshnessStatus === 'current'
                                  ? 'fa-leaf'
                                  : freshnessStatus === 'review_due'
                                    ? 'fa-Triangle-exclamation'
                                    : freshnessStatus === 'expired'
                                      ? 'fa-calendar-xmark'
                                      : 'fa-code-branch'
                              }`}
                            ></i>
                            {freshnessLabelMap[freshnessStatus] || 'CURRENT'}
                          </span>
                        )}

                        {approvalStatus === 'approved' ? (
                          <span className="knowledge-badge approved">
                            <i className="fa-solid fa-circle-check"></i>
                            ACTIVE
                          </span>
                        ) : approvalStatus === 'rejected' ? (
                          <button
                            type="button"
                            className="knowledge-badge rejected"
                            onClick={() => setViewingRejectionSource(item)}
                            title="Click to view rejection reason"
                          >
                            <i className="fa-solid fa-circle-xmark"></i>
                            REJECTED
                          </button>
                        ) : (
                          <span className="knowledge-badge pending_approval">
                            <i className="fa-solid fa-clock"></i>
                            PENDING APPROVAL
                          </span>
                        )}

                        {requiresReviewAction(item) && (
                          <span
                            className="knowledge-badge review-required"
                            title="This information required review. Access is restricted for standard users."
                            data-testid={`badge-review-required-${item.id}`}
                          >
                            <i className="fa-solid fa-clock-rotate-left"></i>
                            REVIEW REQUIRED
                          </span>
                        )}
                      </div>
                    </div>

                    <h3
                      className={`knowledge-card-title cursor-pointer hover:text-blue-600 transition-colors ${
                        requiresReviewAction(item) && !isReviewer ? 'restricted' : ''
                      }`}
                      onClick={() => handleOpenPreview(item)}
                      title={
                        requiresReviewAction(item) && !isReviewer
                          ? 'This information required review — access restricted'
                          : 'Click to live view original source'
                      }
                    >
                      {item.title}
                    </h3>
                    <p className="knowledge-card-description">
                      {item.description ||
                        (item.original_filename
                          ? `File: ${item.original_filename}`
                          : 'Workspace knowledge asset.')}
                    </p>

                    {item.rejection_reason && approvalStatus === 'rejected' && (
                      <div className="knowledge-reject-banner knowledge-exclusion-banner rejected">
                        <div className="knowledge-exclusion-icon-box">
                          <i className="fa-solid fa-triangle-exclamation"></i>
                        </div>
                        <div className="knowledge-exclusion-body">
                          <div className="knowledge-exclusion-header">
                            <strong className="knowledge-exclusion-title">Rejection Reason:</strong>
                          </div>
                          <p className="knowledge-exclusion-desc">{item.rejection_reason}</p>
                        </div>
                      </div>
                    )}

                    {freshnessStatus === 'review_due' && (
                      <div className="knowledge-reject-banner knowledge-exclusion-banner review_due">
                        <div className="knowledge-exclusion-icon-box">
                          <i className="fa-solid fa-triangle-exclamation"></i>
                        </div>
                        <div className="knowledge-exclusion-body">
                          <div className="knowledge-exclusion-header">
                            <strong className="knowledge-exclusion-title">Review Scheduled:</strong>
                            <span className="knowledge-exclusion-badge">Review Due</span>
                          </div>
                          <p className="knowledge-exclusion-desc">
                            This information required review. Document reached its periodic review
                            deadline and cannot be accessed by standard users until verified or
                            renewed.
                          </p>
                          {isReviewer && (
                            <div className="knowledge-exclusion-actions">
                              <button
                                type="button"
                                className="knowledge-exclusion-btn verify"
                                onClick={() => handleVerifySource(item.id, item.title)}
                                disabled={verifyingId === item.id}
                              >
                                <i
                                  className={`fa-solid ${
                                    verifyingId === item.id
                                      ? 'fa-spinner fa-spin'
                                      : 'fa-shield-check'
                                  }`}
                                ></i>
                                <span>Verify &amp; Renew</span>
                              </button>
                              <button
                                type="button"
                                className="knowledge-exclusion-btn supersede"
                                onClick={() => {
                                  setSupersedingSource(item);
                                  setSupersedeReplacementId('');
                                }}
                              >
                                <i className="fa-solid fa-code-branch"></i>
                                <span>Supersede</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {(freshnessStatus === 'expired' || freshnessStatus === 'superseded') && (
                      <div
                        className={`knowledge-reject-banner knowledge-exclusion-banner ${freshnessStatus}`}
                      >
                        <div className="knowledge-exclusion-icon-box">
                          <i
                            className={`fa-solid ${
                              freshnessStatus === 'expired' ? 'fa-calendar-xmark' : 'fa-ban'
                            }`}
                          ></i>
                        </div>
                        <div className="knowledge-exclusion-body">
                          <div className="knowledge-exclusion-header">
                            <strong className="knowledge-exclusion-title">
                              Retrieval Excluded:
                            </strong>
                            <span className="knowledge-exclusion-badge">
                              {freshnessStatus === 'expired' ? 'Expired' : 'Superseded'}
                            </span>
                          </div>
                          <p className="knowledge-exclusion-desc">
                            {freshnessStatus === 'expired'
                              ? 'Expired knowledge cannot be used in RAG until reviewed and renewed.'
                              : 'Superseded by a newer approved knowledge record.'}
                          </p>
                          {isReviewer && freshnessStatus === 'expired' && (
                            <div className="knowledge-exclusion-actions">
                              <button
                                type="button"
                                className="knowledge-exclusion-btn verify"
                                onClick={() => handleVerifySource(item.id, item.title)}
                                disabled={verifyingId === item.id}
                              >
                                <i
                                  className={`fa-solid ${
                                    verifyingId === item.id
                                      ? 'fa-spinner fa-spin'
                                      : 'fa-shield-check'
                                  }`}
                                ></i>
                                <span>Verify &amp; Renew Validity</span>
                              </button>
                              <button
                                type="button"
                                className="knowledge-exclusion-btn supersede"
                                onClick={() => {
                                  setSupersedingSource(item);
                                  setSupersedeReplacementId('');
                                }}
                              >
                                <i className="fa-solid fa-code-branch"></i>
                                <span>Supersede</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="knowledge-card-meta">
                      <span className="knowledge-badge type">{format}</span>
                      {canDelete ? (
                        <button
                          type="button"
                          className={`knowledge-badge knowledge-type-badge interactive ${item.knowledge_type === 'external' ? 'type-external' : 'type-internal'}`}
                          title={`Knowledge Type: ${item.knowledge_type === 'external' ? 'External' : 'Internal'}. Click to switch to ${item.knowledge_type === 'external' ? 'Internal' : 'External'}.`}
                          onClick={() => handleToggleKnowledgeType(item)}
                          disabled={updatingVisibilityId === item.id}
                        >
                          <i
                            className={`fa-solid ${
                              updatingVisibilityId === item.id
                                ? 'fa-spinner fa-spin'
                                : item.knowledge_type === 'external'
                                  ? 'fa-globe'
                                  : 'fa-lock'
                            }`}
                          ></i>
                          {(item.knowledge_type || 'internal').toUpperCase()}
                          <i
                            className="fa-solid fa-repeat knowledge-badge-switch-icon ml-1"
                            title="Switch visibility"
                          ></i>
                        </button>
                      ) : (
                        <span
                          className={`knowledge-badge knowledge-type-badge ${item.knowledge_type === 'external' ? 'type-external' : 'type-internal'}`}
                          title={`Knowledge Type: ${item.knowledge_type === 'external' ? 'External' : 'Internal'}`}
                        >
                          <i
                            className={`fa-solid ${item.knowledge_type === 'external' ? 'fa-globe' : 'fa-lock'}`}
                          ></i>
                          {(item.knowledge_type || 'internal').toUpperCase()}
                        </span>
                      )}
                      <span className="knowledge-badge chunks">
                        <i className="fa-solid fa-vector-square"></i>
                        {chunksCount} chunks
                      </span>
                      {item.created_by_name || item.created_by ? (
                        <span className="knowledge-badge creator">
                          <i className="fa-solid fa-user"></i>
                          {item.created_by_name || item.created_by}
                        </span>
                      ) : null}
                      {item.expires_at && (
                        <span className="knowledge-badge creator" title="Expiration Date">
                          <i className="fa-regular fa-hourglass-half"></i>
                          Exp: {new Date(item.expires_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    <div className="knowledge-card-footer">
                      <span>
                        Updated {updatedAt ? new Date(updatedAt).toLocaleDateString() : 'Recently'}
                      </span>
                      <div className="knowledge-card-actions">
                        <button
                          type="button"
                          className={`knowledge-icon-btn preview ${
                            requiresReviewAction(item) && !isReviewer ? 'restricted' : ''
                          }`}
                          title={
                            requiresReviewAction(item) && !isReviewer
                              ? 'This information required review — access restricted'
                              : 'Live View Original Source'
                          }
                          onClick={() => handleOpenPreview(item)}
                        >
                          <i
                            className={`fa-solid ${
                              requiresReviewAction(item) && !isReviewer
                                ? 'fa-lock text-amber-500'
                                : 'fa-eye'
                            }`}
                          ></i>
                        </button>

                        {/* Reviewer Freshness Actions: Verify/Renew & Supersede */}
                        {isReviewer &&
                          approvalStatus === 'approved' &&
                          freshnessStatus !== 'superseded' && (
                            <>
                              <button
                                type="button"
                                className="knowledge-icon-btn verify"
                                title="Verify & Renew Knowledge Freshness"
                                onClick={() => handleVerifySource(item.id)}
                                disabled={verifyingId === item.id}
                              >
                                <i
                                  className={`fa-solid ${
                                    verifyingId === item.id
                                      ? 'fa-spinner fa-spin'
                                      : 'fa-shield-check'
                                  }`}
                                ></i>
                              </button>
                              <button
                                type="button"
                                className="knowledge-icon-btn supersede"
                                title="Mark as Superseded by Newer Source"
                                onClick={() => {
                                  setSupersedingSource(item);
                                  setSupersedeReplacementId('');
                                }}
                              >
                                <i className="fa-solid fa-code-branch"></i>
                              </button>
                            </>
                          )}

                        {/* Reviewer Actions for Pending Approval */}
                        {isReviewer && approvalStatus === 'pending_approval' && (
                          <>
                            <button
                              type="button"
                              className="knowledge-icon-btn approve"
                              title="Approve Knowledge Source"
                              onClick={() => handleApproveSource(item.id)}
                              disabled={approvingId === item.id}
                            >
                              <i
                                className={`fa-solid ${
                                  approvingId === item.id ? 'fa-spinner fa-spin' : 'fa-check'
                                }`}
                              ></i>
                            </button>
                            <button
                              type="button"
                              className="knowledge-icon-btn reject"
                              title="Reject Knowledge Source"
                              onClick={() => handleOpenRejectModal(item)}
                            >
                              <i className="fa-solid fa-xmark"></i>
                            </button>
                          </>
                        )}
                        {hasStoredFile && (
                          <button
                            type="button"
                            className={`knowledge-icon-btn ${
                              requiresReviewAction(item) && !isReviewer ? 'disabled opacity-50' : ''
                            }`}
                            title={
                              requiresReviewAction(item) && !isReviewer
                                ? 'Cannot download: This information required review'
                                : 'Download Original File'
                            }
                            onClick={() => handleDownloadFile(item.id, item.title)}
                          >
                            <i className="fa-solid fa-download"></i>
                          </button>
                        )}

                        {sourceUrl && (
                          <a
                            href={sourceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="knowledge-icon-btn"
                            title="Open Source Link"
                          >
                            <i className="fa-solid fa-arrow-up-right-from-square"></i>
                          </a>
                        )}

                        {canDelete && (
                          <button
                            type="button"
                            className="knowledge-icon-btn danger"
                            onClick={() => handleDeleteSource(item)}
                            disabled={deletingId === item.id}
                            title="Delete Source"
                          >
                            <i
                              className={`fa-solid ${
                                deletingId === item.id ? 'fa-spinner fa-spin' : 'fa-trash-can'
                              }`}
                            ></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : loading ? (
          <div className="knowledge-loading-state">
            <div className="knowledge-spinner"></div>
            <span>Loading knowledge assets...</span>
          </div>
        ) : (
          <div className="knowledge-empty-state">
            <div className="knowledge-empty-icon">
              <i className="fa-solid fa-folder-open"></i>
            </div>
            <h4 className="knowledge-empty-title">No Knowledge Sources Found</h4>
            <p className="knowledge-empty-text">
              {searchQuery
                ? `No sources matched "${searchQuery}". Try broadening your search or resetting filters.`
                : activeTab === 'pending_approval'
                  ? isReviewer
                    ? 'No submissions are currently pending approval.'
                    : 'You have no submissions currently pending approval.'
                  : activeTab === 'rejected'
                    ? 'No rejected knowledge submissions.'
                    : 'Add your first document (PDF, DOCX) or URL to submit for review and grounding.'}
            </p>
            <button
              type="button"
              className="knowledge-btn knowledge-btn-primary"
              onClick={() => setIsAddModalOpen(true)}
            >
              <i className="fa-solid fa-plus"></i>
              Add First Source
            </button>
          </div>
        )}
      </div>

      {/* ── Reject Reason Modal ── */}
      {rejectingSource && (
        <div className="knowledge-modal-overlay" onClick={() => setRejectingSource(null)}>
          <div className="knowledge-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="knowledge-modal-header">
              <h3 className="knowledge-modal-title">
                <i className="fa-solid fa-triangle-exclamation text-rose-600"></i>
                <span>Reject Knowledge Submission</span>
              </h3>
              <button
                type="button"
                className="knowledge-modal-close"
                onClick={() => setRejectingSource(null)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleConfirmReject}>
              <div className="knowledge-modal-body">
                <p className="text-sm text-slate-600 mb-3">
                  Please provide a clear rejection reason for{' '}
                  <strong>"{rejectingSource.title}"</strong>. The creator will be able to view this
                  feedback.
                </p>
                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">
                    Rejection Reason <span className="knowledge-required-star">*</span>
                  </label>
                  <textarea
                    rows={4}
                    required
                    className="knowledge-form-textarea"
                    placeholder="e.g. Outdated pricing information or unverified claims..."
                    value={rejectionReasonInput}
                    onChange={(e) => setRejectionReasonInput(e.target.value)}
                  />
                </div>
              </div>

              <div className="knowledge-modal-footer">
                <button
                  type="button"
                  className="knowledge-btn knowledge-btn-secondary"
                  onClick={() => setRejectingSource(null)}
                  disabled={submittingReject}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="knowledge-btn knowledge-btn-primary"
                  style={{ backgroundColor: '#e11d48', borderColor: '#e11d48' }}
                  disabled={submittingReject || !rejectionReasonInput.trim()}
                >
                  <i
                    className={`fa-solid ${submittingReject ? 'fa-spinner fa-spin' : 'fa-xmark'}`}
                  ></i>
                  {submittingReject ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Rejection Reason Details Modal ── */}
      {viewingRejectionSource && (
        <div className="knowledge-modal-overlay" onClick={() => setViewingRejectionSource(null)}>
          <div className="knowledge-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="knowledge-modal-header">
              <h3 className="knowledge-modal-title">
                <i className="fa-solid fa-circle-xmark text-rose-600"></i>
                <span>Rejection Feedback</span>
              </h3>
              <button
                type="button"
                className="knowledge-modal-close"
                onClick={() => setViewingRejectionSource(null)}
              >
                &times;
              </button>
            </div>

            <div className="knowledge-modal-body">
              <h4 className="font-semibold text-slate-900 mb-2">{viewingRejectionSource.title}</h4>
              <div className="knowledge-reject-banner text-sm mb-3">
                <p className="font-medium">
                  {viewingRejectionSource.rejection_reason || 'No specific reason provided.'}
                </p>
              </div>
              <p className="text-xs text-slate-500">
                Reviewed by:{' '}
                <strong>{viewingRejectionSource.reviewed_by_name || 'Administrator'}</strong>{' '}
                {viewingRejectionSource.reviewed_at
                  ? `on ${new Date(viewingRejectionSource.reviewed_at).toLocaleString()}`
                  : ''}
              </p>
            </div>

            <div className="knowledge-modal-footer">
              <button
                type="button"
                className="knowledge-btn knowledge-btn-primary"
                onClick={() => setViewingRejectionSource(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Citation Detail Modal ── */}
      {activeCitation && (
        <div className="knowledge-modal-overlay" onClick={() => setActiveCitation(null)}>
          <div
            className="knowledge-modal-dialog knowledge-citation-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="knowledge-modal-header">
              <div className="knowledge-citation-header-wrap">
                <span className="knowledge-citation-header-badge">
                  [C{activeCitation.citationIndex || 1}]
                </span>
                <h3 className="knowledge-modal-title">
                  <span>{activeCitation.sourceTitle}</span>
                </h3>
              </div>
              <button
                type="button"
                className="knowledge-modal-close"
                onClick={() => setActiveCitation(null)}
              >
                &times;
              </button>
            </div>

            <div className="knowledge-modal-body">
              {/* ── Metadata Ribbon ── */}
              <div className="knowledge-citation-ribbon">
                {activeCitation.sourceFormat && (
                  <span className="knowledge-citation-tag">
                    <i className={`fa-solid ${getFormatIcon(activeCitation.sourceFormat)}`}></i>
                    <span>{activeCitation.sourceFormat}</span>
                  </span>
                )}
                {activeCitation.pageNumber && (
                  <span className="knowledge-citation-tag">
                    <i className="fa-solid fa-file-lines text-blue"></i>
                    <span>Page {activeCitation.pageNumber}</span>
                  </span>
                )}
                {activeCitation.originalFilename && (
                  <span className="knowledge-citation-tag">
                    <i className="fa-solid fa-paperclip text-slate-500"></i>
                    <span>{activeCitation.originalFilename}</span>
                  </span>
                )}
                {activeCitation.heading && (
                  <span className="knowledge-citation-tag">
                    <i className="fa-solid fa-heading text-slate-500"></i>
                    <span>{activeCitation.heading}</span>
                  </span>
                )}
                {typeof activeCitation.similarity === 'number' && (
                  <span className="knowledge-citation-tag match">
                    <i className="fa-solid fa-bolt text-emerald-600"></i>
                    <span>{Math.round(activeCitation.similarity * 100)}% Match</span>
                  </span>
                )}
              </div>

              {/* ── Grounded Passage Card ── */}
              {requiresReviewAction(activeCitation) && !isReviewer ? (
                <div
                  className="knowledge-access-restricted-card text-center p-6"
                  data-testid="citation-access-restricted"
                >
                  <div className="knowledge-access-restricted-icon-box">
                    <i className="fa-solid fa-shield-halved"></i>
                  </div>
                  <div className="knowledge-access-restricted-badge">
                    <i className="fa-solid fa-lock"></i>
                    <span>Protected Citation Asset</span>
                  </div>
                  <h4 className="knowledge-access-restricted-title text-base">
                    Citation Excerpt Restricted
                  </h4>
                  <p className="knowledge-access-restricted-message text-xs">
                    This information required review. You cannot access this citation passage until
                    it has been reviewed and verified by an authorized reviewer.
                  </p>
                </div>
              ) : (
                <div className="knowledge-citation-passage-container">
                  <div className="knowledge-citation-passage-header">
                    <div className="knowledge-citation-passage-title">
                      <i className="fa-solid fa-quote-left text-blue"></i>
                      <span>Grounded Source Passage</span>
                    </div>
                    <button
                      type="button"
                      className="knowledge-rag-copy-btn"
                      onClick={handleCopyPassage}
                      title="Copy passage text"
                    >
                      <i
                        className={`fa-solid ${copiedPassage ? 'fa-check text-green' : 'fa-copy'}`}
                      ></i>
                      <span>{copiedPassage ? 'Copied' : 'Copy Passage'}</span>
                    </button>
                  </div>
                  <div className="knowledge-citation-passage-body">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={passageMarkdownComponents}
                    >
                      {cleanHtmlForDisplay(activeCitation.content || activeCitation.excerpt)}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>

            <div className="knowledge-modal-footer knowledge-citation-footer">
              <div className="knowledge-citation-footer-actions">
                {isCitationDownloadable &&
                  (!requiresReviewAction(activeCitation) || isReviewer) && (
                    <button
                      type="button"
                      className="knowledge-btn knowledge-btn-secondary"
                      onClick={() =>
                        handleDownloadFile(activeCitation.sourceId, activeCitation.sourceTitle)
                      }
                    >
                      <i className="fa-solid fa-download mr-1"></i>
                      Download Original
                    </button>
                  )}
                {activeCitation.sourceUrl && (
                  <a
                    href={activeCitation.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="knowledge-btn knowledge-btn-secondary"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square mr-1"></i>
                    Open Source Link
                  </a>
                )}
              </div>
              <button
                type="button"
                className="knowledge-btn knowledge-btn-primary"
                onClick={() => setActiveCitation(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Source Modal ── */}
      {isAddModalOpen && (
        <div className="knowledge-modal-overlay" onClick={() => setIsAddModalOpen(false)}>
          <div className="knowledge-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="knowledge-modal-header">
              <div className="knowledge-modal-header-left">
                <div className="knowledge-modal-icon-badge">
                  <i className="fa-solid fa-cloud-arrow-up"></i>
                </div>
                <div>
                  <h3 className="knowledge-modal-title">Add Knowledge Source</h3>
                  <p className="knowledge-modal-subtitle">
                    Select ingestion method and configure your verified knowledge asset.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="knowledge-modal-close"
                onClick={() => setIsAddModalOpen(false)}
                aria-label="Close modal"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <form onSubmit={handleSaveSource}>
              <div className="knowledge-modal-body">
                {/* ── 3-Option Source Type Cards ── */}
                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">Select Source Type</label>
                  <div className="knowledge-source-type-grid">
                    <button
                      type="button"
                      className={`knowledge-source-type-card ${sourceType === 'file' ? 'active' : ''}`}
                      onClick={() => setSourceType('file')}
                    >
                      {sourceType === 'file' && (
                        <div className="knowledge-source-type-check">
                          <i className="fa-solid fa-check"></i>
                        </div>
                      )}
                      <div className="knowledge-source-type-icon file">
                        <i className="fa-solid fa-file-arrow-up"></i>
                      </div>
                      <div className="knowledge-source-type-title">Upload Document</div>
                      <div className="knowledge-source-type-desc">PDF, DOCX, TXT, MD</div>
                    </button>

                    <button
                      type="button"
                      className={`knowledge-source-type-card ${sourceType === 'web' ? 'active' : ''}`}
                      onClick={() => setSourceType('web')}
                    >
                      {sourceType === 'web' && (
                        <div className="knowledge-source-type-check">
                          <i className="fa-solid fa-check"></i>
                        </div>
                      )}
                      <div className="knowledge-source-type-icon web">
                        <i className="fa-solid fa-globe"></i>
                      </div>
                      <div className="knowledge-source-type-title">Website / URL</div>
                      <div className="knowledge-source-type-desc">Extract from live page</div>
                    </button>

                    <button
                      type="button"
                      className={`knowledge-source-type-card ${sourceType === 'text' ? 'active' : ''}`}
                      onClick={() => setSourceType('text')}
                    >
                      {sourceType === 'text' && (
                        <div className="knowledge-source-type-check">
                          <i className="fa-solid fa-check"></i>
                        </div>
                      )}
                      <div className="knowledge-source-type-icon text">
                        <i className="fa-solid fa-pen-to-square"></i>
                      </div>
                      <div className="knowledge-source-type-title">Custom Text</div>
                      <div className="knowledge-source-type-desc">Write or paste markdown</div>
                    </button>
                  </div>
                </div>

                {/* ── Source Title (Required for all types) ── */}
                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">
                    Source Title <span className="knowledge-required-star">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    className="knowledge-form-input"
                    placeholder={
                      sourceType === 'web'
                        ? 'e.g. Documentation or Pricing Guide'
                        : 'e.g. Enterprise Security Whitepaper'
                    }
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                  />
                </div>

                {/* ── Knowledge Type (Internal / External) ── */}
                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">
                    Knowledge Type <span className="knowledge-required-star">*</span>
                  </label>
                  <div className="knowledge-type-selector-grid">
                    <button
                      type="button"
                      className={`knowledge-type-option-card ${formKnowledgeType === 'internal' ? 'active' : ''}`}
                      onClick={() => setFormKnowledgeType('internal')}
                    >
                      <div className="knowledge-type-option-icon internal">
                        <i className="fa-solid fa-lock"></i>
                      </div>
                      <div className="knowledge-type-option-info">
                        <div className="knowledge-type-option-title">Internal</div>
                        <div className="knowledge-type-option-desc">
                          Private &amp; confidential docs
                        </div>
                      </div>
                      {formKnowledgeType === 'internal' && (
                        <div className="knowledge-type-option-check">
                          <i className="fa-solid fa-circle-check"></i>
                        </div>
                      )}
                    </button>

                    <button
                      type="button"
                      className={`knowledge-type-option-card ${formKnowledgeType === 'external' ? 'active' : ''}`}
                      onClick={() => setFormKnowledgeType('external')}
                    >
                      <div className="knowledge-type-option-icon external">
                        <i className="fa-solid fa-globe"></i>
                      </div>
                      <div className="knowledge-type-option-info">
                        <div className="knowledge-type-option-title">External</div>
                        <div className="knowledge-type-option-desc">
                          Public &amp; client-facing docs
                        </div>
                      </div>
                      {formKnowledgeType === 'external' && (
                        <div className="knowledge-type-option-check">
                          <i className="fa-solid fa-circle-check"></i>
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                {/* ── Mode-Specific Fields ── */}
                {sourceType === 'file' && (
                  <div className="knowledge-form-group">
                    <label className="knowledge-form-label">
                      Document File <span className="knowledge-required-star">*</span>
                    </label>
                    <div
                      className="knowledge-file-dropzone"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <i className="fa-solid fa-cloud-arrow-up knowledge-dropzone-icon"></i>
                      <span className="knowledge-dropzone-text">
                        <strong>Click to browse</strong> or drag and drop document
                      </span>
                      <span className="knowledge-dropzone-subtext">
                        PDF, DOCX, TXT, MD (up to 25MB)
                      </span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.docx,.txt,.md"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                      />
                    </div>
                    {selectedFile && (
                      <div className="knowledge-file-preview">
                        <div className="knowledge-file-preview-left">
                          <div className="knowledge-file-preview-icon">
                            <i className="fa-solid fa-file-lines"></i>
                          </div>
                          <div className="knowledge-file-preview-details">
                            <span className="knowledge-file-preview-name">{selectedFile.name}</span>
                            <span className="knowledge-file-preview-size">
                              {Math.round(selectedFile.size / 1024)} KB
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="knowledge-file-preview-remove"
                          title="Remove file"
                          onClick={() => {
                            setSelectedFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                        >
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {sourceType === 'web' && (
                  <div className="knowledge-form-group">
                    <label className="knowledge-form-label">
                      Target Website URL <span className="knowledge-required-star">*</span>
                    </label>
                    <div className="knowledge-input-icon-wrap">
                      <i className="fa-solid fa-link knowledge-input-leading-icon"></i>
                      <input
                        type="url"
                        required
                        className="knowledge-form-input with-leading-icon"
                        placeholder="https://yourcompany.com/docs/pricing"
                        value={formUrl}
                        onChange={(e) => setFormUrl(e.target.value)}
                      />
                    </div>
                    <span className="knowledge-form-help">
                      <i className="fa-solid fa-circle-info text-blue-500"></i>
                      <span>
                        Web page content will be automatically fetched, parsed, and chunked into
                        vector embeddings.
                      </span>
                    </span>
                  </div>
                )}

                {sourceType === 'text' && (
                  <div className="knowledge-form-group">
                    <label className="knowledge-form-label">
                      Document / Notes Content <span className="knowledge-required-star">*</span>
                    </label>
                    <div className="knowledge-quill-wrapper">
                      <ReactQuill
                        theme="snow"
                        value={formContent}
                        onChange={setFormContent}
                        placeholder="Write, compose, or paste rich text documentation, guidelines, FAQs, or policies..."
                        modules={quillModules}
                        formats={quillFormats}
                      />
                    </div>
                    <span className="knowledge-form-help">
                      <i className="fa-solid fa-circle-info text-purple-500"></i>
                      <span>
                        Rich text formatting (headings, lists, checklists, quotes, and code) will be
                        cleanly parsed and vector indexed.
                      </span>
                    </span>
                  </div>
                )}

                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">
                    Brief Description <span className="knowledge-label-optional">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    className="knowledge-form-input"
                    placeholder="Brief description of what this knowledge covers..."
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                  />
                </div>

                {/* ── Module A3: Freshness, Review & Expiration Rules ── */}
                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">Freshness &amp; Lifecycle Schedule</label>
                  <div className="knowledge-freshness-inputs-grid">
                    <div>
                      <span className="knowledge-sublabel">
                        Effective From <span className="knowledge-required-star">*</span>
                      </span>
                      <input
                        type="date"
                        required
                        min={todayStr}
                        className="knowledge-form-input"
                        aria-label="Effective From Date"
                        value={formEffectiveFrom}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormEffectiveFrom(val);
                          if (formReviewDueAt && formReviewDueAt < val) {
                            setFormReviewDueAt(val);
                          }
                          if (formExpiresAt && formExpiresAt < val) {
                            setFormExpiresAt(val);
                          }
                        }}
                      />
                    </div>
                    <div>
                      <span className="knowledge-sublabel">
                        Review Due Date <span className="knowledge-required-star">*</span>
                      </span>
                      <input
                        type="date"
                        required
                        min={formEffectiveFrom || todayStr}
                        className="knowledge-form-input"
                        aria-label="Review Due Date"
                        value={formReviewDueAt}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormReviewDueAt(val);
                          if (formExpiresAt && formExpiresAt < val) {
                            setFormExpiresAt(val);
                          }
                        }}
                      />
                    </div>
                    <div>
                      <span className="knowledge-sublabel">
                        Expiration Date <span className="knowledge-required-star">*</span>
                      </span>
                      <input
                        type="date"
                        required
                        min={formReviewDueAt || formEffectiveFrom || todayStr}
                        className="knowledge-form-input"
                        aria-label="Expiration Date"
                        value={formExpiresAt}
                        onChange={(e) => setFormExpiresAt(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {sources.filter((s) => s.approval_status === 'approved').length > 0 && (
                  <div className="knowledge-form-group">
                    <label className="knowledge-form-label">
                      Replaces / Supersedes Existing Source{' '}
                      <span className="knowledge-label-optional">(Optional)</span>
                    </label>
                    <select
                      className="knowledge-form-input"
                      aria-label="Supersedes Existing Source"
                      value={formSupersedesSourceId}
                      onChange={(e) => setFormSupersedesSourceId(e.target.value)}
                    >
                      <option value="">— Does not supersede an existing record —</option>
                      {sources
                        .filter(
                          (s) =>
                            s.approval_status === 'approved' && s.freshness_status !== 'superseded'
                        )
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.title}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="knowledge-modal-footer">
                <button
                  type="button"
                  className="knowledge-btn knowledge-btn-secondary"
                  onClick={() => setIsAddModalOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="knowledge-btn knowledge-btn-primary"
                  disabled={submitting}
                >
                  <i
                    className={`fa-solid ${
                      submitting ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-up'
                    }`}
                  ></i>
                  {submitting ? 'Ingesting & Indexing...' : 'Submit Knowledge'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Live Source Viewer Modal (Redesigned) ── */}
      {previewSource && (
        <div className="knowledge-modal-overlay" onClick={() => setPreviewSource(null)}>
          <div
            className="knowledge-modal-dialog knowledge-preview-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="knowledge-preview-header">
              <div className="knowledge-preview-header-wrap">
                <div
                  className={`knowledge-preview-type-icon ${getFormatBadgeClass(previewSource.format)}`}
                >
                  <i className={`fa-solid ${getFormatIcon(previewSource.format)}`}></i>
                </div>
                <div className="knowledge-preview-header-meta">
                  <h3 className="knowledge-preview-title" title={previewSource.title}>
                    <span>{previewSource.title}</span>
                  </h3>
                  <div className="knowledge-preview-badges">
                    <span className="knowledge-preview-pill type">
                      {previewSource.format || 'DOCUMENT'}
                    </span>
                    {Boolean(
                      isReviewer ||
                      (previewSource.created_by_user_id &&
                        previewSource.created_by_user_id === userId) ||
                      (previewSource.created_by && previewSource.created_by === username)
                    ) ? (
                      <button
                        type="button"
                        className={`knowledge-preview-pill interactive ${previewSource.knowledge_type === 'external' ? 'type-external' : 'type-internal'}`}
                        title={`Knowledge Type: ${previewSource.knowledge_type === 'external' ? 'External' : 'Internal'}. Click to switch to ${previewSource.knowledge_type === 'external' ? 'Internal' : 'External'}.`}
                        onClick={() => handleToggleKnowledgeType(previewSource)}
                        disabled={updatingVisibilityId === previewSource.id}
                      >
                        <i
                          className={`fa-solid ${
                            updatingVisibilityId === previewSource.id
                              ? 'fa-spinner fa-spin'
                              : previewSource.knowledge_type === 'external'
                                ? 'fa-globe'
                                : 'fa-lock'
                          } mr-1`}
                        ></i>
                        {(previewSource.knowledge_type || 'internal').toUpperCase()}
                        <i
                          className="fa-solid fa-repeat ml-1.5 opacity-70"
                          title="Toggle visibility"
                        ></i>
                      </button>
                    ) : (
                      <span
                        className={`knowledge-preview-pill ${previewSource.knowledge_type === 'external' ? 'type-external' : 'type-internal'}`}
                        title={`Knowledge Type: ${previewSource.knowledge_type === 'external' ? 'External' : 'Internal'}`}
                      >
                        <i
                          className={`fa-solid ${previewSource.knowledge_type === 'external' ? 'fa-globe' : 'fa-lock'} mr-1`}
                        ></i>
                        {(previewSource.knowledge_type || 'internal').toUpperCase()}
                      </span>
                    )}
                    <span
                      className={`knowledge-preview-pill ${previewSource.approval_status || 'pending_approval'}`}
                    >
                      {previewSource.approval_status === 'approved' ? (
                        <>
                          <span className="knowledge-status-pulse-dot"></span>
                          <span>ACTIVE</span>
                        </>
                      ) : previewSource.approval_status === 'rejected' ? (
                        <>
                          <i className="fa-solid fa-xmark"></i>
                          <span>REJECTED</span>
                        </>
                      ) : (
                        <>
                          <i className="fa-regular fa-clock"></i>
                          <span>PENDING APPROVAL</span>
                        </>
                      )}
                    </span>
                    {previewSource.category && (
                      <span className="knowledge-preview-pill category">
                        {previewSource.category}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="knowledge-preview-actions">
                {previewFileUrl && (
                  <a
                    href={previewFileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="knowledge-preview-action-btn"
                    title="Open in Fullscreen / New Tab"
                  >
                    <i className="fa-solid fa-expand"></i>
                  </a>
                )}
                {previewSource.source_url && (
                  <a
                    href={previewSource.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="knowledge-preview-action-btn"
                    title="Open Source URL"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square"></i>
                  </a>
                )}
                {Boolean(
                  previewSource.storage_key ||
                  (previewSource.original_filename &&
                    previewSource.source_type !== 'web' &&
                    previewSource.source_type !== 'text' &&
                    previewSource.format !== 'URL' &&
                    previewSource.format !== 'WEB')
                ) &&
                  (!requiresReviewAction(previewSource) || isReviewer) && (
                    <button
                      type="button"
                      className="knowledge-preview-action-btn"
                      onClick={() => handleDownloadFile(previewSource.id, previewSource.title)}
                      title="Download Original Document"
                    >
                      <i className="fa-solid fa-download"></i>
                    </button>
                  )}
                <button
                  type="button"
                  className="knowledge-preview-action-btn close"
                  onClick={() => setPreviewSource(null)}
                  title="Close Viewer"
                >
                  &times;
                </button>
              </div>
            </div>

            {/* Rejection notice banner if rejected */}
            {previewSource.approval_status === 'rejected' && previewSource.rejection_reason && (
              <div className="knowledge-preview-rejection-banner">
                <i className="fa-solid fa-circle-exclamation"></i>
                <div>
                  <strong>Rejection Note: </strong>
                  <span>{previewSource.rejection_reason}</span>
                  {previewSource.reviewed_by_name && (
                    <span className="ml-2 font-medium opacity-75">
                      — Reviewed by {previewSource.reviewed_by_name}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Review action required banners */}
            {requiresReviewAction(previewSource) && !isReviewer && (
              <div
                className="knowledge-preview-restricted-banner"
                data-testid="knowledge-preview-restricted-banner"
              >
                <div className="knowledge-preview-restricted-banner-icon">
                  <i className="fa-solid fa-lock"></i>
                </div>
                <div>
                  <strong>Access Restricted: </strong>
                  <span>
                    This information required review. Standard users cannot access this knowledge
                    until the required review action is completed.
                  </span>
                </div>
              </div>
            )}

            {requiresReviewAction(previewSource) && isReviewer && (
              <div
                className="knowledge-preview-review-action-banner"
                data-testid="knowledge-preview-reviewer-banner"
              >
                <div className="knowledge-preview-review-action-banner-icon">
                  <i className="fa-solid fa-triangle-exclamation"></i>
                </div>
                <div>
                  <strong>Review Action Required: </strong>
                  <span>
                    This information required review. Regular workspace users cannot access this
                    knowledge until reviewed and verified. Review the document below to approve,
                    verify, or supersede.
                  </span>
                </div>
              </div>
            )}

            {/* Ribbon meta strip */}
            <div className="knowledge-preview-ribbon">
              <div className="knowledge-preview-ribbon-item">
                <i className="fa-regular fa-user text-slate-400"></i>
                <span>
                  Added by:{' '}
                  <strong>
                    {previewSource.created_by_name || previewSource.created_by || 'Workspace User'}
                  </strong>
                </span>
              </div>
              <span className="knowledge-preview-ribbon-divider"></span>
              <div className="knowledge-preview-ribbon-item">
                <i className="fa-solid fa-cubes text-blue"></i>
                <span>
                  <strong>{previewSource.chunks_count ?? previewSource.chunksCount ?? 0}</strong>{' '}
                  Vector Chunks
                </span>
              </div>
              <span className="knowledge-preview-ribbon-divider"></span>
              <div className="knowledge-preview-ribbon-item">
                <i className="fa-solid fa-coins text-amber-500"></i>
                <span>
                  <strong>
                    {(previewSource.token_count ?? previewSource.tokenCount ?? 0).toLocaleString()}
                  </strong>{' '}
                  Tokens
                </span>
              </div>
              {previewSource.original_filename && (
                <>
                  <span className="knowledge-preview-ribbon-divider"></span>
                  <div className="knowledge-preview-ribbon-item">
                    <i className="fa-solid fa-paperclip text-slate-400"></i>
                    <span>{previewSource.original_filename}</span>
                  </div>
                </>
              )}
              {previewSource.created_at && (
                <>
                  <span className="knowledge-preview-ribbon-divider"></span>
                  <div className="knowledge-preview-ribbon-item">
                    <i className="fa-regular fa-calendar text-slate-400"></i>
                    <span>{new Date(previewSource.created_at).toLocaleDateString()}</span>
                  </div>
                </>
              )}
            </div>

            {/* Sub-tabs Bar */}
            {(!requiresReviewAction(previewSource) || isReviewer) && (
              <div className="knowledge-preview-tabs-bar">
                <div className="knowledge-preview-segmented-control">
                  <button
                    type="button"
                    className={`knowledge-preview-tab-btn ${previewActiveTab === 'content' ? 'active' : ''}`}
                    onClick={() => setPreviewActiveTab('content')}
                  >
                    <i className="fa-solid fa-file-lines"></i>
                    <span>Live Content &amp; Document</span>
                  </button>
                  <button
                    type="button"
                    className={`knowledge-preview-tab-btn ${previewActiveTab === 'chunks' ? 'active' : ''}`}
                    onClick={() => setPreviewActiveTab('chunks')}
                  >
                    <i className="fa-solid fa-cubes-stacked"></i>
                    <span>Vector Chunks Breakdown</span>
                    <span className="knowledge-tab-count-badge">
                      {previewFullSource?.chunks?.length || previewSource.chunks_count || 0}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Body */}
            <div className="knowledge-preview-body">
              {previewLoading ? (
                <div className="knowledge-loading-state">
                  <div className="knowledge-spinner"></div>
                  <span>Loading live document preview...</span>
                </div>
              ) : (requiresReviewAction(previewSource) || previewFullSource?.accessRestricted) &&
                !isReviewer ? (
                <div
                  className="knowledge-access-restricted-container"
                  data-testid="knowledge-access-restricted-view"
                >
                  <div className="knowledge-access-restricted-card">
                    <div className="knowledge-access-restricted-icon-box">
                      <i className="fa-solid fa-shield-halved"></i>
                    </div>
                    <div className="knowledge-access-restricted-badge">
                      <i className="fa-solid fa-lock"></i>
                      <span>Protected Knowledge Asset</span>
                    </div>
                    <h3 className="knowledge-access-restricted-title">Access Restricted</h3>
                    <p className="knowledge-access-restricted-message">
                      This information required review. You cannot access this knowledge until it
                      has been reviewed and verified by an authorized reviewer.
                    </p>
                    <div className="knowledge-access-restricted-note">
                      <i className="fa-solid fa-clock-rotate-left mr-1.5 text-amber-600"></i>
                      <span>
                        A review action is currently scheduled or pending for this document.
                      </span>
                    </div>
                  </div>
                </div>
              ) : previewActiveTab === 'content' ? (
                previewSource.format === 'PDF' && previewFileUrl ? (
                  <div className="knowledge-live-pdf-container">
                    <div className="knowledge-live-pdf-toolbar">
                      <div className="flex items-center gap-2">
                        <i className="fa-solid fa-file-pdf text-rose-500"></i>
                        <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                          <button
                            type="button"
                            className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                              previewPdfViewMode === 'pdf'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-slate-600 hover:text-slate-900'
                            }`}
                            onClick={() => setPreviewPdfViewMode('pdf')}
                          >
                            <i className="fa-solid fa-eye mr-1"></i> Interactive PDF
                          </button>
                          <button
                            type="button"
                            className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                              previewPdfViewMode === 'text'
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-slate-600 hover:text-slate-900'
                            }`}
                            onClick={() => setPreviewPdfViewMode('text')}
                          >
                            <i className="fa-solid fa-align-left mr-1"></i> Extracted Text
                          </button>
                        </div>
                      </div>
                      <div className="knowledge-live-pdf-tools">
                        <a
                          href={previewFileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="knowledge-btn knowledge-btn-secondary"
                          style={{ padding: '0.25rem 0.625rem', fontSize: '0.75rem' }}
                        >
                          <i className="fa-solid fa-expand mr-1"></i> Fullscreen
                        </a>
                        <button
                          type="button"
                          className="knowledge-btn knowledge-btn-secondary"
                          style={{ padding: '0.25rem 0.625rem', fontSize: '0.75rem' }}
                          onClick={() => handleDownloadFile(previewSource.id, previewSource.title)}
                        >
                          <i className="fa-solid fa-download mr-1"></i> Download
                        </button>
                      </div>
                    </div>

                    {previewPdfViewMode === 'pdf' ? (
                      <iframe
                        src={previewFileUrl}
                        className="knowledge-live-pdf-iframe"
                        title={`Live PDF Viewer - ${previewSource.title}`}
                      />
                    ) : (
                      <div
                        className="knowledge-live-reader-container"
                        style={{ borderTop: 'none', borderRadius: '0 0 12px 12px' }}
                      >
                        <div className="knowledge-doc-stats-strip">
                          <span className="knowledge-doc-stat-pill">
                            <i className="fa-solid fa-clock text-blue"></i> ~{docStats.readMinutes}{' '}
                            min read
                          </span>
                          <span className="knowledge-doc-stat-pill">
                            <i className="fa-solid fa-file-word text-emerald-600"></i>{' '}
                            {docStats.words.toLocaleString()} words
                          </span>
                          <span className="knowledge-doc-stat-pill">
                            <i className="fa-solid fa-coins text-amber-500"></i>{' '}
                            {docStats.tokens.toLocaleString()} tokens
                          </span>
                          <span className="knowledge-doc-stat-pill">
                            <i className="fa-solid fa-cubes text-indigo-500"></i> {docStats.chunks}{' '}
                            vector chunks
                          </span>
                          <button
                            type="button"
                            className="knowledge-rag-copy-btn ml-auto"
                            onClick={handleCopyPreviewText}
                            title="Copy full text content"
                          >
                            <i
                              className={`fa-solid ${
                                copiedPreviewContent ? 'fa-check text-green' : 'fa-copy'
                              }`}
                            ></i>
                            <span>{copiedPreviewContent ? 'Copied' : 'Copy Content'}</span>
                          </button>
                        </div>
                        <div className="knowledge-live-reader-content">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={passageMarkdownComponents}
                          >
                            {cleanHtmlForDisplay(
                              previewFullSource?.content ||
                                previewSource.content ||
                                previewSource.description ||
                                'No text content available.'
                            )}
                          </ReactMarkdown>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="knowledge-live-reader-container">
                    <div className="knowledge-doc-stats-strip">
                      <span className="knowledge-doc-stat-pill">
                        <i className="fa-solid fa-clock text-blue"></i> ~{docStats.readMinutes} min
                        read
                      </span>
                      <span className="knowledge-doc-stat-pill">
                        <i className="fa-solid fa-file-word text-emerald-600"></i>{' '}
                        {docStats.words.toLocaleString()} words
                      </span>
                      <span className="knowledge-doc-stat-pill">
                        <i className="fa-solid fa-coins text-amber-500"></i>{' '}
                        {docStats.tokens.toLocaleString()} tokens
                      </span>
                      <span className="knowledge-doc-stat-pill">
                        <i className="fa-solid fa-cubes text-indigo-500"></i> {docStats.chunks}{' '}
                        vector chunks
                      </span>
                      <button
                        type="button"
                        className="knowledge-rag-copy-btn ml-auto"
                        onClick={handleCopyPreviewText}
                        title="Copy full content text"
                      >
                        <i
                          className={`fa-solid ${
                            copiedPreviewContent ? 'fa-check text-green' : 'fa-copy'
                          }`}
                        ></i>
                        <span>{copiedPreviewContent ? 'Copied' : 'Copy Content'}</span>
                      </button>
                    </div>

                    <div className="knowledge-live-reader-content">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={passageMarkdownComponents}
                      >
                        {cleanHtmlForDisplay(
                          previewFullSource?.content ||
                            previewSource.content ||
                            previewSource.description ||
                            'No text content available.'
                        )}
                      </ReactMarkdown>
                    </div>
                  </div>
                )
              ) : (
                <div className="knowledge-chunks-breakdown-wrap">
                  <div className="knowledge-chunks-toolbar">
                    <div className="knowledge-chunks-search-box">
                      <i className="fa-solid fa-magnifying-glass text-slate-400 text-xs"></i>
                      <input
                        type="text"
                        placeholder="Search chunks by keyword, heading, or page number..."
                        value={previewChunkSearch}
                        onChange={(e) => setPreviewChunkSearch(e.target.value)}
                      />
                      {previewChunkSearch && (
                        <button
                          type="button"
                          onClick={() => setPreviewChunkSearch('')}
                          className="text-slate-400 hover:text-slate-600 text-xs"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                    <div className="knowledge-chunks-counter">
                      Showing {displayedChunks.length} of{' '}
                      {previewFullSource?.chunks?.length || previewSource.chunks_count || 0} chunks
                    </div>
                  </div>

                  <div className="knowledge-chunks-grid">
                    {displayedChunks && displayedChunks.length > 0 ? (
                      displayedChunks.map((ch, idx) => (
                        <div key={ch.id || idx} className="knowledge-chunk-card">
                          <div className="knowledge-chunk-header">
                            <span className="knowledge-chunk-index-badge">
                              Chunk #{(ch.chunk_index ?? idx) + 1}
                            </span>
                            <div className="knowledge-chunk-tags">
                              {ch.page_number && (
                                <span className="knowledge-chunk-tag">
                                  <i className="fa-regular fa-file"></i> Page {ch.page_number}
                                </span>
                              )}
                              {ch.heading && (
                                <span className="knowledge-chunk-tag" title={ch.heading}>
                                  <i className="fa-solid fa-heading"></i> {ch.heading}
                                </span>
                              )}
                              {ch.token_count && (
                                <span className="knowledge-chunk-tag">
                                  <i className="fa-solid fa-coins"></i> {ch.token_count} tokens
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              className="knowledge-rag-copy-btn ml-auto"
                              onClick={() => handleCopyChunk(ch.id || idx, ch.content)}
                              title="Copy chunk text"
                            >
                              <i
                                className={`fa-solid ${
                                  copiedChunkId === (ch.id || idx)
                                    ? 'fa-check text-green'
                                    : 'fa-copy'
                                }`}
                              ></i>
                              <span>{copiedChunkId === (ch.id || idx) ? 'Copied' : 'Copy'}</span>
                            </button>
                          </div>
                          <div className="knowledge-chunk-content-wrap">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={passageMarkdownComponents}
                            >
                              {cleanHtmlForDisplay(ch.content)}
                            </ReactMarkdown>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-slate-500 text-sm">
                        {previewChunkSearch
                          ? `No chunks match "${previewChunkSearch}".`
                          : 'No chunks breakdown available for this source.'}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="knowledge-modal-footer">
              <div className="flex items-center gap-2">
                {isReviewer && previewSource.approval_status === 'pending_approval' && (
                  <>
                    <button
                      type="button"
                      className="knowledge-btn knowledge-btn-primary"
                      style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
                      onClick={async () => {
                        await handleApproveSource(previewSource.id);
                        setPreviewSource(null);
                      }}
                    >
                      <i className="fa-solid fa-check mr-1"></i> Approve &amp; Publish
                    </button>
                    <button
                      type="button"
                      className="knowledge-btn knowledge-btn-secondary"
                      style={{ color: '#e11d48', borderColor: '#fda4af' }}
                      onClick={() => {
                        const target = previewSource;
                        setPreviewSource(null);
                        handleOpenRejectModal(target);
                      }}
                    >
                      <i className="fa-solid fa-xmark mr-1"></i> Reject
                    </button>
                  </>
                )}

                {isReviewer &&
                  previewSource.approval_status === 'approved' &&
                  previewSource.freshness_status !== 'superseded' && (
                    <>
                      <button
                        type="button"
                        className="knowledge-btn knowledge-btn-primary"
                        style={{ backgroundColor: '#059669', borderColor: '#059669' }}
                        onClick={async () => {
                          const target = previewSource;
                          setPreviewSource(null);
                          await handleVerifySource(target.id, target.title);
                        }}
                      >
                        <i className="fa-solid fa-shield-check mr-1"></i> Verify &amp; Renew
                        Freshness
                      </button>
                      <button
                        type="button"
                        className="knowledge-btn knowledge-btn-secondary"
                        onClick={() => {
                          const target = previewSource;
                          setPreviewSource(null);
                          setSupersedingSource(target);
                          setSupersedeReplacementId('');
                        }}
                      >
                        <i className="fa-solid fa-code-branch mr-1"></i> Supersede with Newer Source
                      </button>
                    </>
                  )}
              </div>

              <button
                type="button"
                className="knowledge-btn knowledge-btn-secondary"
                onClick={() => setPreviewSource(null)}
              >
                Close Viewer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Supersede Source Modal (Module A) ── */}
      {supersedingSource && (
        <div className="knowledge-modal-overlay" onClick={() => setSupersedingSource(null)}>
          <div className="knowledge-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="knowledge-modal-header">
              <h3 className="knowledge-modal-title">
                <i className="fa-solid fa-code-branch text-indigo-600"></i>
                <span>Mark Knowledge Record as Superseded</span>
              </h3>
              <button
                type="button"
                className="knowledge-modal-close"
                onClick={() => setSupersedingSource(null)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleConfirmSupersede}>
              <div className="knowledge-modal-body">
                <p className="text-sm text-slate-600 mb-3">
                  Select the newer approved knowledge record that replaces{' '}
                  <strong>"{supersedingSource.title}"</strong>. Once superseded, the old record will
                  be excluded from RAG production retrieval while preserving its audit trail.
                </p>
                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">
                    Replacement Approved Source <span className="knowledge-required-star">*</span>
                  </label>
                  <select
                    required
                    className="knowledge-form-input"
                    value={supersedeReplacementId}
                    onChange={(e) => setSupersedeReplacementId(e.target.value)}
                  >
                    <option value="">— Select replacement knowledge source —</option>
                    {sources
                      .filter(
                        (s) =>
                          s.id !== supersedingSource.id &&
                          s.approval_status === 'approved' &&
                          s.freshness_status !== 'superseded'
                      )
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.title}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="knowledge-modal-footer">
                <button
                  type="button"
                  className="knowledge-btn knowledge-btn-secondary"
                  onClick={() => setSupersedingSource(null)}
                  disabled={submittingSupersede}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="knowledge-btn knowledge-btn-primary"
                  disabled={submittingSupersede || !supersedeReplacementId}
                >
                  <i
                    className={`fa-solid ${
                      submittingSupersede ? 'fa-spinner fa-spin' : 'fa-code-branch'
                    }`}
                  ></i>
                  {submittingSupersede ? 'Updating...' : 'Confirm Supersession'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Resolve RAG Feedback Modal (Module B3 & D1) ── */}
      {resolvingFeedbackItem && (
        <div className="knowledge-modal-overlay" onClick={() => setResolvingFeedbackItem(null)}>
          <div
            className="knowledge-modal-dialog knowledge-triage-modal-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="knowledge-modal-header">
              <div className="knowledge-triage-modal-title-wrap">
                <div className="knowledge-modal-icon-badge indigo">
                  <i className="fa-solid fa-sliders"></i>
                </div>
                <div>
                  <h3 className="knowledge-modal-title">Triage &amp; Corrective Resolution</h3>
                  <p className="knowledge-modal-subtext">
                    Record root-cause remediation and lifecycle transition
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="knowledge-modal-close"
                onClick={() => setResolvingFeedbackItem(null)}
                aria-label="Close modal"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <form onSubmit={handleConfirmResolveFeedback} data-testid="resolve-feedback-form">
              <div className="knowledge-modal-body">
                <div className="knowledge-triage-modal-summary">
                  <div className="knowledge-triage-summary-row">
                    <span className="knowledge-badge rejected">
                      <i className="fa-solid fa-thumbs-down mr-1"></i> Not Helpful
                    </span>
                    {resolvingFeedbackItem.reason &&
                      renderReasonBadge(resolvingFeedbackItem.reason)}
                    <time className="knowledge-feedback-timestamp ml-auto">
                      <i className="fa-regular fa-clock mr-1"></i>{' '}
                      {formatFeedbackDate(resolvingFeedbackItem.created_at)}
                    </time>
                  </div>

                  <div className="knowledge-triage-question-box">
                    <span className="knowledge-triage-label">User Question</span>
                    <p className="knowledge-triage-question-text">
                      {resolvingFeedbackItem.question}
                    </p>
                  </div>

                  {resolvingFeedbackItem.comment && (
                    <div className="knowledge-triage-quote-box">
                      <div className="knowledge-triage-quote-label">
                        <i className="fa-solid fa-comment-dots mr-1.5 text-indigo-500"></i>
                        <span>User feedback comment:</span>
                      </div>
                      <p className="knowledge-triage-quote-text">
                        &ldquo;{resolvingFeedbackItem.comment}&rdquo;
                      </p>
                    </div>
                  )}

                  {resolvingFeedbackItem.linked_source_title && (
                    <div className="knowledge-triage-source-row">
                      <span
                        className="knowledge-feedback-source-pill"
                        title={resolvingFeedbackItem.linked_source_title}
                      >
                        <i className="fa-regular fa-file-lines text-slate-400 mr-1.5"></i>
                        <span>
                          Source: <strong>{resolvingFeedbackItem.linked_source_title}</strong>
                        </span>
                      </span>
                    </div>
                  )}
                </div>

                <div className="knowledge-triage-grid-row">
                  <div className="knowledge-form-group">
                    <label className="knowledge-form-label">
                      Feedback Lifecycle Status <span className="knowledge-required-star">*</span>
                    </label>
                    <select
                      className="knowledge-form-input"
                      value={resolveStatusInput}
                      onChange={(e) => setResolveStatusInput(e.target.value)}
                    >
                      <option value="new">New</option>
                      <option value="under_review">Under Review</option>
                      <option value="action_required">Action Required</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </div>

                  <div className="knowledge-form-group">
                    <label className="knowledge-form-label">
                      Corrective Action Taken <span className="knowledge-required-star">*</span>
                    </label>
                    <select
                      className="knowledge-form-input"
                      value={resolveActionInput}
                      onChange={(e) => setResolveActionInput(e.target.value)}
                    >
                      <option value="verified_knowledge">Verified &amp; renewed source</option>
                      <option value="added_replacement_source">Added new replacement source</option>
                      <option value="superseded_outdated_source">
                        Marked source as superseded
                      </option>
                      <option value="updated_retrieval_config">
                        Adjusted retrieval / threshold
                      </option>
                      <option value="added_eval_case">Added to regression evaluation</option>
                      <option value="no_action_needed">No knowledge change required</option>
                    </select>
                  </div>
                </div>

                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">Resolution Notes</label>
                  <textarea
                    rows={3}
                    className="knowledge-form-textarea"
                    placeholder="Document root cause, source modifications, or prompt adjustments..."
                    value={resolveNotesInput}
                    onChange={(e) => setResolveNotesInput(e.target.value)}
                  />
                </div>

                <div
                  className={`knowledge-promote-eval-card ${resolvePromoteEval ? 'is-active' : ''}`}
                  onClick={() => setResolvePromoteEval(!resolvePromoteEval)}
                >
                  <div className="knowledge-promote-eval-checkbox-wrap">
                    <input
                      type="checkbox"
                      className="knowledge-promote-eval-checkbox"
                      checked={resolvePromoteEval}
                      onChange={(e) => setResolvePromoteEval(e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="knowledge-promote-eval-content">
                    <span className="knowledge-promote-eval-title">
                      Promote to Versioned RAG Evaluation Test Suite
                    </span>
                    <span className="knowledge-promote-eval-desc">
                      Automatically registers this failure case as a permanent regression test to
                      guard against future model or prompt regressions.
                    </span>
                  </div>
                </div>
              </div>

              <div className="knowledge-modal-footer">
                <button
                  type="button"
                  className="knowledge-btn knowledge-btn-secondary"
                  onClick={() => setResolvingFeedbackItem(null)}
                  disabled={submittingResolveFb}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="knowledge-btn knowledge-btn-primary"
                  disabled={submittingResolveFb}
                >
                  <i
                    className={`fa-solid ${
                      submittingResolveFb ? 'fa-spinner fa-spin' : 'fa-check'
                    } mr-1.5`}
                  ></i>
                  {submittingResolveFb ? 'Saving...' : 'Save Resolution'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Add Evaluation Case Modal (Module C1) ── */}
      {isAddEvalCaseModalOpen && (
        <div className="knowledge-modal-overlay" onClick={() => setIsAddEvalCaseModalOpen(false)}>
          <div className="knowledge-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="knowledge-modal-header">
              <h3 className="knowledge-modal-title">
                <i className="fa-solid fa-flask-vial text-blue-600"></i>
                <span>Add RAG Evaluation Test Case</span>
              </h3>
              <button
                type="button"
                className="knowledge-modal-close"
                onClick={() => setIsAddEvalCaseModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleCreateEvalCase}>
              <div className="knowledge-modal-body">
                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">
                    Test Question <span className="knowledge-required-star">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    className="knowledge-form-input"
                    placeholder="e.g. How many days of paid annual leave do employees receive?"
                    value={evalCaseQuestion}
                    onChange={(e) => setEvalCaseQuestion(e.target.value)}
                  />
                </div>

                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">Evaluation Category</label>
                  <select
                    className="knowledge-form-input"
                    value={evalCaseCategory}
                    onChange={(e) => {
                      const nextCat = e.target.value;
                      setEvalCaseCategory(nextCat);
                      if (
                        [
                          'missing_information',
                          'irrelevant_results',
                          'access_restrictions',
                          'unanswerable_abstention',
                          'outdated_expired',
                        ].includes(nextCat)
                      ) {
                        setEvalCaseExpectedBehavior('abstain');
                      } else {
                        setEvalCaseExpectedBehavior('answer');
                      }
                    }}
                  >
                    <option value="correct_retrieval">Correct Knowledge Retrieval</option>
                    <option value="outdated_expired">
                      Outdated or Expired Knowledge Exclusion
                    </option>
                    <option value="conflicting_sources">Conflicting Knowledge Sources</option>
                    <option value="missing_information">Missing Information</option>
                    <option value="irrelevant_results">Irrelevant Search Results</option>
                    <option value="access_restrictions">Organization Access Restrictions</option>
                    <option value="unanswerable_abstention">
                      Unanswerable Question (Abstention)
                    </option>
                    <option value="multi_document">Multi-Document Synthesis</option>
                  </select>
                </div>

                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">Expected System Behavior</label>
                  <select
                    className="knowledge-form-input"
                    value={evalCaseExpectedBehavior}
                    onChange={(e) => setEvalCaseExpectedBehavior(e.target.value)}
                  >
                    <option value="answer">Provide Grounded Answer with Citation</option>
                    <option value="abstain">Abstain Safely (Insufficient Evidence)</option>
                    <option value="exclude_expired">Exclude Expired / Superseded Source</option>
                  </select>
                </div>

                {evalCaseExpectedBehavior === 'answer' && (
                  <>
                    <div className="knowledge-form-group">
                      <label className="knowledge-form-label">
                        Expected Knowledge Source{' '}
                        <span className="knowledge-label-optional">(Optional)</span>
                      </label>
                      <select
                        className="knowledge-form-input"
                        value={evalCaseExpectedSourceId}
                        onChange={(e) => setEvalCaseExpectedSourceId(e.target.value)}
                      >
                        <option value="">— Any matching approved source —</option>
                        {sources
                          .filter((s) => s.approval_status === 'approved')
                          .map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.title}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="knowledge-form-group">
                      <label className="knowledge-form-label">
                        Reference / Expected Answer{' '}
                        <span className="knowledge-label-optional">(Optional)</span>
                      </label>
                      <textarea
                        rows={3}
                        className="knowledge-form-textarea"
                        placeholder="Expected reference answer for correctness comparison..."
                        value={evalCaseExpectedAnswer}
                        onChange={(e) => setEvalCaseExpectedAnswer(e.target.value)}
                      />
                    </div>

                    <div className="knowledge-form-group">
                      <label className="knowledge-form-label">
                        Required Keywords / Facts{' '}
                        <span className="knowledge-label-optional">(Comma-separated)</span>
                      </label>
                      <input
                        type="text"
                        className="knowledge-form-input"
                        placeholder="e.g. 18 days, annual leave, carryover"
                        value={evalCaseExpectedKeywords}
                        onChange={(e) => setEvalCaseExpectedKeywords(e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="knowledge-modal-footer">
                <button
                  type="button"
                  className="knowledge-btn knowledge-btn-secondary"
                  onClick={() => setIsAddEvalCaseModalOpen(false)}
                  disabled={submittingEvalCase}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="knowledge-btn knowledge-btn-primary"
                  disabled={submittingEvalCase || !evalCaseQuestion.trim()}
                >
                  <i
                    className={`fa-solid ${submittingEvalCase ? 'fa-spinner fa-spin' : 'fa-plus'}`}
                  ></i>
                  {submittingEvalCase ? 'Adding...' : 'Save Evaluation Case'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ── Modify Evaluation Case Modal (Module C1 & C3) ── */}
      {editingEvalCase && (
        <div className="knowledge-modal-overlay" onClick={() => setEditingEvalCase(null)}>
          <div className="knowledge-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="knowledge-modal-header">
              <h3 className="knowledge-modal-title">
                <i className="fa-solid fa-pen-to-square text-blue-600"></i>
                <span>Update Evaluation Test Case</span>
              </h3>
              <button
                type="button"
                className="knowledge-modal-close"
                onClick={() => setEditingEvalCase(null)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleUpdateEvalCase}>
              <div className="knowledge-modal-body">
                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">
                    Test Question <span className="knowledge-required-star">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    className="knowledge-form-input"
                    placeholder="e.g. How many days of paid annual leave do employees receive?"
                    value={editCaseQuestion}
                    onChange={(e) => setEditCaseQuestion(e.target.value)}
                  />
                </div>

                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">Evaluation Category</label>
                  <select
                    className="knowledge-form-input"
                    value={editCaseCategory}
                    onChange={(e) => {
                      const nextCat = e.target.value;
                      setEditCaseCategory(nextCat);
                      if (
                        [
                          'missing_information',
                          'irrelevant_results',
                          'access_restrictions',
                          'unanswerable_abstention',
                          'outdated_expired',
                        ].includes(nextCat)
                      ) {
                        setEditCaseExpectedBehavior('abstain');
                      } else {
                        setEditCaseExpectedBehavior('answer');
                      }
                    }}
                  >
                    <option value="correct_retrieval">Correct Knowledge Retrieval</option>
                    <option value="outdated_expired">
                      Outdated or Expired Knowledge Exclusion
                    </option>
                    <option value="conflicting_sources">Conflicting Knowledge Sources</option>
                    <option value="missing_information">Missing Information</option>
                    <option value="irrelevant_results">Irrelevant Search Results</option>
                    <option value="access_restrictions">Organization Access Restrictions</option>
                    <option value="unanswerable_abstention">
                      Unanswerable Question (Abstention)
                    </option>
                    <option value="multi_document">Multi-Document Synthesis</option>
                  </select>
                </div>

                <div className="knowledge-form-group">
                  <label className="knowledge-form-label">Expected System Behavior</label>
                  <select
                    className="knowledge-form-input"
                    value={editCaseExpectedBehavior}
                    onChange={(e) => setEditCaseExpectedBehavior(e.target.value)}
                  >
                    <option value="answer">Provide Grounded Answer with Citation</option>
                    <option value="abstain">Abstain Safely (Insufficient Evidence)</option>
                    <option value="exclude_expired">Exclude Expired / Superseded Source</option>
                  </select>
                </div>

                {editCaseExpectedBehavior === 'answer' && (
                  <>
                    <div className="knowledge-form-group">
                      <label className="knowledge-form-label">
                        Expected Knowledge Source{' '}
                        <span className="knowledge-label-optional">(Optional)</span>
                      </label>
                      <select
                        className="knowledge-form-input"
                        value={editCaseExpectedSourceId}
                        onChange={(e) => setEditCaseExpectedSourceId(e.target.value)}
                      >
                        <option value="">— Any matching approved source —</option>
                        {sources
                          .filter((s) => s.approval_status === 'approved')
                          .map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.title}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="knowledge-form-group">
                      <label className="knowledge-form-label">
                        Reference / Expected Answer{' '}
                        <span className="knowledge-label-optional">(Optional)</span>
                      </label>
                      <textarea
                        rows={3}
                        className="knowledge-form-textarea"
                        placeholder="Expected reference answer for correctness comparison..."
                        value={editCaseExpectedAnswer}
                        onChange={(e) => setEditCaseExpectedAnswer(e.target.value)}
                      />
                    </div>

                    <div className="knowledge-form-group">
                      <label className="knowledge-form-label">
                        Required Keywords / Facts{' '}
                        <span className="knowledge-label-optional">(Comma-separated)</span>
                      </label>
                      <input
                        type="text"
                        className="knowledge-form-input"
                        placeholder="e.g. 18 days, annual leave, carryover"
                        value={editCaseExpectedKeywords}
                        onChange={(e) => setEditCaseExpectedKeywords(e.target.value)}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="knowledge-modal-footer">
                <button
                  type="button"
                  className="knowledge-btn knowledge-btn-secondary"
                  onClick={() => setEditingEvalCase(null)}
                  disabled={submittingUpdateCase}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="knowledge-btn knowledge-btn-primary"
                  disabled={submittingUpdateCase || !editCaseQuestion.trim()}
                >
                  <i
                    className={`fa-solid ${
                      submittingUpdateCase ? 'fa-spinner fa-spin' : 'fa-check'
                    }`}
                  ></i>
                  {submittingUpdateCase ? 'Saving...' : 'Update Test Case'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Hard Delete Confirmation Modal for Knowledge Source ── */}
      {deletingSourceTarget && (
        <div
          className="knowledge-modal-overlay"
          onClick={() => !deletingId && setDeletingSourceTarget(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-source-modal-title"
        >
          <div
            className="knowledge-modal-dialog"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '520px' }}
          >
            <div className="knowledge-modal-header">
              <div className="knowledge-modal-header-left">
                <div className="knowledge-modal-icon-badge danger">
                  <i className="fa-solid fa-triangle-exclamation"></i>
                </div>
                <div>
                  <h3 id="delete-source-modal-title" className="knowledge-modal-title">
                    Permanently Delete Source
                  </h3>
                  <p className="knowledge-modal-subtitle">
                    Hard deletion is permanent and cannot be undone.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="knowledge-modal-close"
                onClick={() => !deletingId && setDeletingSourceTarget(null)}
                disabled={Boolean(deletingId)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="knowledge-modal-body">
              <div
                style={{
                  padding: '0.875rem 1rem',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.375rem',
                }}
              >
                <span
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    color: '#64748b',
                    letterSpacing: '0.05em',
                  }}
                >
                  Resource Target
                </span>
                <div
                  style={{
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    color: '#1e293b',
                    wordBreak: 'break-word',
                  }}
                >
                  {deletingSourceTarget.title ||
                    deletingSourceTarget.original_file_name ||
                    'Untitled Source'}
                </div>
                {deletingSourceTarget.source_type && (
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Type:{' '}
                    <span
                      style={{
                        textTransform: 'capitalize',
                        fontWeight: 500,
                        color: '#334155',
                      }}
                    >
                      {deletingSourceTarget.source_type}
                    </span>
                    {deletingSourceTarget.chunks_count
                      ? ` • ${deletingSourceTarget.chunks_count} vector chunks`
                      : ''}
                  </div>
                )}
              </div>

              <div
                style={{
                  padding: '0.875rem 1rem',
                  backgroundColor: '#fef2f2',
                  borderRadius: '8px',
                  border: '1px solid #fecaca',
                  color: '#991b1b',
                  fontSize: '0.8125rem',
                  lineHeight: 1.5,
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start',
                }}
              >
                <i
                  className="fa-solid fa-circle-exclamation"
                  style={{ color: '#dc2626', marginTop: '2px', flexShrink: 0 }}
                ></i>
                <div>
                  <strong>Permanent Data Removal:</strong>
                  <ul style={{ margin: '0.375rem 0 0 1rem', padding: 0, listStyleType: 'disc' }}>
                    <li>All associated vector embeddings and chunks will be purged.</li>
                    <li>Source files in cloud storage will be permanently deleted.</li>
                    <li>
                      AI search and generation pipelines will immediately stop citing this content.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="knowledge-modal-footer">
              <button
                type="button"
                className="knowledge-btn knowledge-btn-secondary"
                onClick={() => setDeletingSourceTarget(null)}
                disabled={Boolean(deletingId)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="knowledge-btn knowledge-btn-danger"
                data-testid="confirm-delete-source-btn"
                onClick={handleConfirmDeleteSource}
                disabled={Boolean(deletingId)}
              >
                <i className={`fa-solid ${deletingId ? 'fa-spinner fa-spin' : 'fa-trash-can'}`}></i>
                {deletingId ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Remove Evaluation Test Case Confirmation Modal ── */}
      {deletingEvalCaseTarget && (
        <div
          className="knowledge-modal-overlay"
          onClick={() => !deletingCaseId && setDeletingEvalCaseTarget(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-eval-modal-title"
        >
          <div
            className="knowledge-modal-dialog"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '500px' }}
          >
            <div className="knowledge-modal-header">
              <div className="knowledge-modal-header-left">
                <div className="knowledge-modal-icon-badge danger">
                  <i className="fa-solid fa-triangle-exclamation"></i>
                </div>
                <div>
                  <h3 id="delete-eval-modal-title" className="knowledge-modal-title">
                    Remove Evaluation Test Case
                  </h3>
                  <p className="knowledge-modal-subtitle">
                    Remove this test case from the automated evaluation suite.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="knowledge-modal-close"
                onClick={() => !deletingCaseId && setDeletingEvalCaseTarget(null)}
                disabled={Boolean(deletingCaseId)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="knowledge-modal-body">
              <div
                style={{
                  padding: '0.875rem 1rem',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.375rem',
                }}
              >
                <span
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    color: '#64748b',
                    letterSpacing: '0.05em',
                  }}
                >
                  Benchmark Question
                </span>
                <div
                  style={{
                    fontSize: '0.875rem',
                    color: '#1e293b',
                    fontStyle: 'italic',
                    wordBreak: 'break-word',
                  }}
                >
                  "
                  {deletingEvalCaseTarget.question ||
                    deletingEvalCaseTarget.query ||
                    'Evaluation Test Case'}
                  "
                </div>
              </div>

              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#64748b', lineHeight: 1.5 }}>
                This golden test case will be removed from the active RAG evaluation dataset and
                will no longer be benchmarked in regression runs.
              </p>
            </div>

            <div className="knowledge-modal-footer">
              <button
                type="button"
                className="knowledge-btn knowledge-btn-secondary"
                onClick={() => setDeletingEvalCaseTarget(null)}
                disabled={Boolean(deletingCaseId)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="knowledge-btn knowledge-btn-danger"
                data-testid="confirm-remove-case-btn"
                onClick={handleConfirmDeleteEvalCase}
                disabled={Boolean(deletingCaseId)}
              >
                <i
                  className={`fa-solid ${deletingCaseId ? 'fa-spinner fa-spin' : 'fa-trash-can'}`}
                ></i>
                {deletingCaseId ? 'Removing...' : 'Remove Test Case'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Image Lightbox Modal ── */}
      {zoomedImage && (
        <div
          className="knowledge-modal-overlay knowledge-image-lightbox-overlay"
          onClick={() => setZoomedImage(null)}
        >
          <div className="knowledge-image-lightbox-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="knowledge-image-lightbox-header">
              <div className="knowledge-image-lightbox-header-left">
                <div className="knowledge-image-lightbox-icon-badge">
                  <i className="fa-solid fa-image"></i>
                </div>
                <div>
                  <h3 className="knowledge-image-lightbox-title">
                    {zoomedImage.alt || 'Grounded Source Diagram'}
                  </h3>
                  <p className="knowledge-image-lightbox-subtitle">High-resolution diagram view</p>
                </div>
              </div>
              <div className="knowledge-image-lightbox-actions">
                {zoomedImage.src && (
                  <a
                    href={zoomedImage.src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="knowledge-btn knowledge-btn-secondary knowledge-btn-sm"
                    title="Open original image in new tab"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square"></i>
                    <span>Open in New Tab</span>
                  </a>
                )}
                <button
                  type="button"
                  className="knowledge-modal-close"
                  onClick={() => setZoomedImage(null)}
                  title="Close viewer"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
            </div>
            <div className="knowledge-image-lightbox-body">
              <img
                src={zoomedImage.src}
                alt={zoomedImage.alt || 'Full size diagram'}
                className="knowledge-image-lightbox-img"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
